import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, X, Plus, Download, Upload, FileSpreadsheet, Loader2, AlertTriangle, Award, RotateCcw, Wrench, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import Pagination from '../../components/Pagination';
import { CompOffRecheckDialog } from '../../components/hr/CompOffRecheckDialog';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const API = process.env.REACT_APP_BACKEND_URL;

// Status palette · updated (EL/CO added; CL/SL/SPL/ML hidden from picker)
const STATUS_COLORS = {
  P: 'bg-emerald-500 text-white',
  H: 'bg-amber-400 text-slate-900',
  A: 'bg-red-500 text-white',
  WFH: 'bg-blue-500 text-white',
  WO: 'bg-slate-300 text-slate-700',
  HO: 'bg-sky-200 text-sky-900',
  L: 'bg-violet-500 text-white',
  EL: 'bg-emerald-600 text-white',
  PL: 'bg-violet-600 text-white',
  SL: 'bg-pink-500 text-white',
  CL: 'bg-purple-500 text-white',
  SPL: 'bg-fuchsia-400 text-white',
  ML: 'bg-rose-500 text-white',
  CO: 'bg-amber-500 text-white',
  LWP: 'bg-zinc-700 text-white',
};
const STATUS_LABELS = {
  P: 'Present',
  H: 'Half Day',
  A: 'Absent',
  WFH: 'Work From Home',
  WO: 'Weekly Off',
  HO: 'Holiday',
  L: 'Leave (legacy)',
  EL: 'Earned Leave',
  PL: 'Privilege Leave',
  SL: 'Sick Leave',
  CL: 'Casual Leave',
  SPL: 'Short Permission',
  ML: 'Maternity Leave',
  CO: 'Comp-off',
  LWP: 'Leave Without Pay',
};
const STATUS_ORDER = ['P', 'H', 'A', 'WFH', 'WO', 'HO', 'EL', 'PL', 'CO', 'LWP'];

function formatYM(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function AttendanceLeave() {
  const [tab, setTab] = useUrlTab('tab', 'attendance');
  return (
    <div className="space-y-4" data-testid="attendance-leave">
      <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 text-xs text-indigo-900">
        <strong>HR Manual Marking:</strong> Click any cell to mark the day. Codes P/H/A/WFH/WO/HO are direct status; EL/PL/CO/LWP auto-create an Approved leave record and decrement the employee&apos;s balance. Mobile self-service punch will replace this when launched.
      </div>
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'attendance', label: 'Attendance Roster' },
          { id: 'leaves', label: 'Leave Workflow' },
          { id: 'compoff', label: 'Comp-Off Ledger' },
          { id: 'leave-ledger', label: 'Leave Ledger' },
          { id: 'accrual', label: 'Accrual' },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`subtab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? 'text-[#3b4872] border-[#3b4872]' : 'text-slate-500 border-transparent hover:text-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'attendance' && <AttendanceMatrix />}
      {tab === 'leaves' && <LeaveWorkflow />}
      {tab === 'compoff' && <CompOffLedger />}
      {tab === 'leave-ledger' && <LeaveLedger />}
      {tab === 'accrual' && (<><LeaveAccrualPanel /><YearEndLapsePanel /></>)}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Leave-accrual backfill (ChangeRequest A-Payroll §2) — dry-run Preview then
// Commit. Skips employee/type pairs that already have an HR manual adjustment.
// ════════════════════════════════════════════════════════════════════════
function LeaveAccrualPanel() {
  const [fromMonth, setFromMonth] = useState('2026-05');
  const [toMonth, setToMonth] = useState('2026-08');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [runs, setRuns] = useState([]);

  const loadRuns = useCallback(async () => {
    try { const d = await apiGet('/hr/leave-accrual/runs', { limit: 10 }); setRuns(d.runs || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const runPreview = async () => {
    setLoading(true);
    try {
      const d = await apiGet('/hr/leave-accrual/preview', { from_month: fromMonth, to_month: toMonth });
      setPreview(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const commit = async () => {
    const typed = window.prompt('Type COMMIT to write these accrual credits to balances. This cannot be undone.');
    if (typed !== 'COMMIT') { if (typed !== null) toast.error('Confirmation text did not match.'); return; }
    setCommitting(true);
    try {
      const d = await apiPost(`/hr/leave-accrual/commit?from_month=${fromMonth}&to_month=${toMonth}`, {});
      const t = d.totals || {};
      toast.success(`Committed: ${t.credited_entries} credited, ${t.held_entries} held, ${t.skipped_manual} skipped`);
      loadRuns();
      runPreview();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'Only super_admin / HR Manager can commit.' : (e?.response?.data?.detail || 'Commit failed'));
    } finally { setCommitting(false); }
  };

  const t = preview?.totals;
  return (
    <Card data-testid="leave-accrual-panel">
      <CardContent className="p-4 space-y-4">
        <div>
          <h3 className="font-display font-bold text-sm">Leave-accrual backfill</h3>
          <p className="text-xs text-slate-500 mt-1 leading-snug">
            Credits PL 1.5 + EL 0.5 per completed month for a historical range (the automatic engine only runs from
            its go-live month). Probation months are <strong>held</strong> and released on confirmation.
            Employee/type pairs with an existing HR manual balance adjustment are <strong>skipped</strong> and listed for you to reconcile.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">From</label>
            <input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="accrual-from" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">To</label>
            <input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="accrual-to" />
          </div>
          <Button onClick={runPreview} disabled={loading} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="accrual-preview-btn">
            {loading ? 'Computing…' : 'Preview'}
          </Button>
          {preview && (
            <Button onClick={commit} disabled={committing || (t?.credited_entries || 0) + (t?.held_entries || 0) === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="accrual-commit-btn">
              {committing ? 'Committing…' : 'Commit'}
            </Button>
          )}
        </div>

        {t && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm" data-testid="accrual-totals">
            <div className="bg-emerald-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Credited</div><div className="font-bold text-emerald-700">{t.credited_entries} · {t.credited_days}d</div></div>
            <div className="bg-amber-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Held (probation)</div><div className="font-bold text-amber-700">{t.held_entries} · {t.held_days}d</div></div>
            <div className="bg-sky-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Skipped — manual</div><div className="font-bold text-sky-700">{t.skipped_manual}</div></div>
            <div className="bg-slate-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Skipped — existing</div><div className="font-bold">{t.skipped_existing}</div></div>
            <div className="bg-rose-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Anomalies (no DOJ)</div><div className="font-bold text-rose-700">{t.anomalies}</div></div>
          </div>
        )}

        {preview && (preview.skipped_manual || []).length > 0 && (
          <div className="border rounded-sm">
            <div className="bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800">Skipped — manual credit exists ({preview.skipped_manual.length})</div>
            <div className="overflow-auto max-h-48">
              <table className="w-full text-xs">
                <tbody>
                  {preview.skipped_manual.slice(0, 500).map((s, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`accrual-skip-${i}`}>
                      <td className="px-2 py-1 font-mono">{s.emp_id}</td>
                      <td className="px-2 py-1">{s.name}</td>
                      <td className="px-2 py-1">{s.leave_type}</td>
                      <td className="px-2 py-1">{s.month}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {runs.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Recent runs</div>
            <table className="w-full text-xs" data-testid="accrual-runs">
              <thead className="text-slate-400 text-[10px] uppercase">
                <tr><th className="text-left py-1">When</th><th className="text-left py-1">Trigger</th><th className="text-left py-1">Range</th><th className="text-right py-1">Credited/Held/Skipped</th><th className="text-left py-1">Actor</th></tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="py-1">{(r.started_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-1">{r.dry_run ? 'preview' : r.trigger}</td>
                    <td className="py-1">{r.from_month || '—'} → {r.to_month}</td>
                    <td className="py-1 text-right">{r.totals?.credited_entries || 0}/{r.totals?.held_entries || 0}/{(r.totals?.skipped_manual || 0) + (r.totals?.skipped_existing || 0)}</td>
                    <td className="py-1">{r.actor || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════
// M4 · Year-end leave lapse / carry-forward — 31-Dec rules. Dry-run Preview
// then a typed-confirmation Commit, mirroring the accrual panel above.
// EL lapses to zero; PL carries forward uncapped into the new year.
// ════════════════════════════════════════════════════════════════════════
function YearEndLapsePanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const run = async (dryRun) => {
    if (!dryRun) {
      const typed = window.prompt(`Type COMMIT to apply the ${year} year-end lapse/carry-forward. This cannot be undone.`);
      if (typed !== 'COMMIT') { if (typed !== null) toast.error('Confirmation text did not match.'); return; }
    }
    dryRun ? setLoading(true) : setCommitting(true);
    try {
      const d = await apiPost('/hr/leave/year-end-lapse', { year, dry_run: dryRun });
      setPreview(d);
      if (!dryRun) toast.success(`Committed: EL ${d.totals?.el_lapsed || 0}d lapsed, PL ${d.totals?.pl_carried || 0}d carried to ${year + 1}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Year-end run failed');
    } finally {
      dryRun ? setLoading(false) : setCommitting(false);
    }
  };

  const t = preview?.totals;
  return (
    <Card data-testid="year-end-lapse-panel">
      <CardContent className="p-4 space-y-4">
        <div>
          <h3 className="font-display font-bold text-sm">Year-end lapse / carry-forward (31-Dec)</h3>
          <p className="text-xs text-slate-500 mt-1 leading-snug">
            EL lapses to zero at year-end. PL's remaining balance carries forward, <strong>uncapped</strong>, into the
            new year. Comp-Off is untouched — it never expires on its own. Reads the same balance the ledger shows
            right now, so preview and commit can never disagree with what employees see.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 block mb-1">Year</label>
            <input type="number" value={year} onChange={e => setYear(parseInt(e.target.value, 10) || year)} className="border border-slate-200 rounded-md px-3 py-2 text-sm w-28" data-testid="year-end-year-input" />
          </div>
          <Button onClick={() => run(true)} disabled={loading} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="year-end-preview-btn">
            {loading ? 'Computing…' : 'Preview'}
          </Button>
          {preview && (
            <Button onClick={() => run(false)} disabled={committing || preview.dry_run === false} className="bg-emerald-600 hover:bg-emerald-700" data-testid="year-end-commit-btn">
              {committing ? 'Committing…' : preview.dry_run === false ? 'Committed' : 'Commit'}
            </Button>
          )}
        </div>
        {t && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm" data-testid="year-end-totals">
            <div className="bg-rose-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">EL lapsed</div><div className="font-bold text-rose-700">{t.el_lapsed || 0}d</div></div>
            <div className="bg-emerald-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">PL carried to {year + 1}</div><div className="font-bold text-emerald-700">{t.pl_carried || 0}d</div></div>
            <div className="bg-slate-50 border rounded-sm p-3"><div className="text-[10px] uppercase text-slate-500">Employees processed</div><div className="font-bold">{preview.employees_processed}</div></div>
          </div>
        )}
        {preview && preview.results?.length > 0 && (
          <div className="border rounded-sm">
            <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">Per-employee ({preview.results.length})</div>
            <div className="overflow-auto max-h-64">
              <table className="w-full text-xs">
                <thead className="text-slate-400 text-[10px] uppercase sticky top-0 bg-white">
                  <tr><th className="text-left py-1 px-2">Emp</th><th className="text-left py-1 px-2">Name</th><th className="text-right py-1 px-2">EL lapse</th><th className="text-right py-1 px-2">PL carry</th></tr>
                </thead>
                <tbody>
                  {preview.results.slice(0, 500).map((r, i) => (
                    <tr key={r.employee_id} className="border-t border-slate-100" data-testid={`year-end-row-${i}`}>
                      <td className="px-2 py-1 font-mono">{r.emp_id}</td>
                      <td className="px-2 py-1">{r.name}</td>
                      <td className="px-2 py-1 text-right">{r.el?.lapse ?? 0}</td>
                      <td className="px-2 py-1 text-right">{r.pl?.carry_forward ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Comp-Off Ledger — who earned compensatory offs (Sundays/holidays worked),
// their available balance, and a liability alert for high accumulations.
// ════════════════════════════════════════════════════════════════════════
function CompOffLedger() {
  const { user } = useAuth();
  const dept = String(user?.department || '').trim().toUpperCase();
  const canRecheck = user?.role === 'super_admin' || dept === 'IT' || dept === 'HR' || dept.startsWith('HUMAN');
  const [showRecheck, setShowRecheck] = useState(false);
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        fetch(`${API}/api/hr/comp-off/summary`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/hr/comp-off`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ rows: [] })),
      ]);
      setSummary(s);
      setLedger(Array.isArray(l?.rows) ? l.rows : []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = summary?.rows || [];

  return (
    <Card data-testid="compoff-ledger">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-2">
          <Award size={15} className="text-[#3b4872]" /> Compensatory Off Ledger
          {canRecheck && (
            <button onClick={() => setShowRecheck(true)} data-testid="compoff-recheck-btn"
              className="ml-auto normal-case tracking-normal text-xs font-bold px-3 py-1.5 border border-[#3b4872] text-[#3b4872] rounded hover:bg-indigo-50 flex items-center gap-1.5">
              <RotateCcw size={12} /> Recheck comp offs
            </button>
          )}
        </CardTitle>
        {showRecheck && <CompOffRecheckDialog onClose={() => setShowRecheck(false)} onApplied={load} />}
      </CardHeader>
      <CardContent>
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Total Available</div>
            <div className="font-display font-extrabold text-2xl mt-1 tabular-nums" data-testid="compoff-total-available">{summary?.total_available ?? 0}<span className="text-xs text-slate-400 ml-1">days</span></div>
          </div>
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50">
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Employees w/ Balance</div>
            <div className="font-display font-extrabold text-2xl mt-1 tabular-nums">{summary?.employees_with_balance ?? 0}</div>
          </div>
          <div className={`border rounded-md p-3 ${summary?.high_balance_count ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50/50'}`}>
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">High Balance (≥{summary?.high_threshold ?? 5})</div>
            <div className="font-display font-extrabold text-2xl mt-1 tabular-nums text-amber-700">{summary?.high_balance_count ?? 0}</div>
          </div>
        </div>

        {summary?.high_balance_count > 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 mb-4" data-testid="compoff-alert">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span><strong>{summary.high_balance_count} employee{summary.high_balance_count > 1 ? 's have' : ' has'}</strong> ≥ {summary.high_threshold} unused comp-offs — a growing leave liability. Encourage them to avail comp-offs or settle as per policy.</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-400 text-sm"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm italic">No comp-offs earned yet. They accrue when an employee is Present on a Sunday or public holiday.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Emp Code</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Name</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Earned</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Used</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Pending</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Available</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Last Earned</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.employee_id || i} className={`${i % 2 ? 'bg-slate-50/40' : ''} ${r.high_balance ? 'bg-amber-50/60' : ''}`} data-testid={`compoff-row-${r.emp_code}`}>
                    <td className="py-2 px-2 font-mono">{r.emp_code || '—'}</td>
                    <td className="py-2 px-2 font-display font-semibold">{r.employee_name || '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.earned}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-500">{r.used}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-400">{r.pending || 0}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-display font-bold">
                      {r.available}
                      {r.high_balance && <AlertTriangle size={11} className="inline ml-1 text-amber-600" />}
                    </td>
                    <td className="py-2 px-2 tabular-nums text-slate-500">{r.last_earned_date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Recent credit detail */}
        {ledger.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 mb-2">Recent Credits · {ledger.length}</div>
            <div className="flex flex-wrap gap-2">
              {ledger.filter(c => c.date && !c.entry_type).slice(0, 40).map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1.5 text-[11px] border border-slate-200 rounded-full px-2.5 py-1 bg-white">
                  <span className="font-mono text-slate-500">{c.date}</span>
                  <span className="font-display font-semibold">{c.employee_name}</span>
                  <span className="text-emerald-700 font-bold">+{c.credit}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Leave Ledger — HR view of EL / PL / CO balances per employee + manual credit
// ════════════════════════════════════════════════════════════════════════
// ── Leave Balance Import Wizard ──────────────────────────────────────────────
function LeaveBalanceImportWizard({ onClose, onDone }) {
  const [step, setStep] = useState(0); // 0=upload, 1=preview, 2=done
  const [file, setFile] = useState(null);
  const [asOnDate, setAsOnDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includedLeaveIds, setIncludedLeaveIds] = useState(new Set());
  const fileRef = useRef(null);

  const analyze = async () => {
    if (!file || !asOnDate) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('as_on_date', asOnDate);
      const r = await fetch(`${API}/api/hr/leave-ledger/import/preview`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Preview failed'); }
      const d = await r.json();
      setPreview(d);
      setIncludedLeaveIds(new Set());
      setStep(1);
    } catch (e) { toast.error(e.message || 'Analysis failed'); }
    setLoading(false);
  };

  const toggleIncluded = (leaveId) => {
    setIncludedLeaveIds(prev => {
      const next = new Set(prev);
      if (next.has(leaveId)) next.delete(leaveId); else next.add(leaveId);
      return next;
    });
  };

  const s = preview?.summary || {};
  const ready = (s.ready || 0) + (s.warnings || 0);
  const hasErrors = (s.errors || 0) > 0;

  const commit = async () => {
    if (!preview || hasErrors) return;
    setLoading(true);
    try {
      const commitRows = (preview.rows || []).filter(r => r.status !== 'error');
      const r = await apiPost('/hr/leave-ledger/import/commit', {
        rows: commitRows, year: preview.year, as_on_date: asOnDate,
        included_leave_ids: Array.from(includedLeaveIds),
      });
      setResult(r);
      setStep(2);
      toast.success(`Import done: ${r.inserted} new · ${r.replaced} replaced`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Commit failed'); }
    setLoading(false);
  };

  // Flatten pending EL/PL leaves across rows for the tick UI (M9)
  const pendingItems = [];
  (preview?.rows || []).forEach(r => {
    Object.entries(r.pending_leaves || {}).forEach(([ltype, list]) => {
      (list || []).forEach(p => pendingItems.push({ ...p, leave_type: ltype, employee_name: r.employee_name, emp_id: r.emp_id }));
    });
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <div>
            <div className="font-display font-bold text-base">Leave Opening Balance Import</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Step {step + 1}/3 · {['Upload sheet', 'Review', 'Done'][step]} · super_admin commits
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="leave-import-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-sm">
          {step === 0 && (
            <>
              <p className="text-slate-600">Upload an Excel file with columns: <code className="bg-slate-100 px-1 rounded text-xs">Employee ID · EL · PL · C/Off</code>. Opening balances will replace existing values (SET semantics).</p>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">This sheet is "as on" date *</label>
                <input type="date" value={asOnDate} onChange={e => setAsOnDate(e.target.value)}
                  className="border border-slate-300 rounded px-3 py-2 text-sm" data-testid="leave-import-as-on-date" />
                <div className="text-[11px] text-slate-400 mt-1">The date HR's Excel figures are reconciled as of — used to surface pending leave that may already be reflected in the sheet.</div>
              </div>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#3b4872] hover:bg-slate-50 transition-colors"
                data-testid="leave-import-drop"
              >
                <FileSpreadsheet size={32} className="mx-auto text-slate-400 mb-2" />
                {file ? (
                  <div className="font-semibold text-[#3b4872]">{file.name}</div>
                ) : (
                  <div className="text-slate-500">Click to choose Excel (.xlsx)</div>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={e => setFile(e.target.files?.[0] || null)} />
            </>
          )}

          {step === 1 && preview && (
            <>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Ready', val: s.ready, color: 'emerald' },
                  { label: 'Warnings', val: s.warnings, color: 'amber' },
                  { label: 'Errors', val: s.errors, color: 'red' },
                ].map(({ label, val, color }) => (
                  <div key={label} className={`border border-${color}-200 bg-${color}-50 rounded p-3 text-center`}>
                    <div className={`text-2xl font-extrabold text-${color}-700`}>{val || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
                  </div>
                ))}
              </div>
              {hasErrors && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 font-semibold" data-testid="leave-import-hard-block">
                  {s.errors} row(s) have errors — fix them in the Excel and re-upload. Nothing can be imported until this sheet is clean.
                </div>
              )}
              <div className="border border-slate-200 rounded overflow-hidden">
                <div className="overflow-y-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        {['Row', 'Emp ID', 'Name', 'EL', 'PL', 'CO', 'Status'].map(h => (
                          <th key={h} className="px-2 py-1.5 text-left text-[10px] uppercase font-bold text-slate-500 border-b">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.rows || []).map((r, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${r.status === 'error' ? 'bg-red-50' : r.status === 'warning' ? 'bg-amber-50' : 'bg-white'}`}>
                          <td className="px-2 py-1 text-slate-400 font-mono">{r.row}</td>
                          <td className="px-2 py-1 font-mono text-slate-600">{r.emp_id}</td>
                          <td className="px-2 py-1 text-slate-700">{r.employee_name || '—'}</td>
                          <td className="px-2 py-1 text-center text-emerald-700 font-bold">{r.el ?? '—'}</td>
                          <td className="px-2 py-1 text-center text-violet-700 font-bold">{r.pl ?? '—'}</td>
                          <td className="px-2 py-1 text-center text-amber-700 font-bold">{r.co ?? '—'}</td>
                          <td className="px-2 py-1">
                            {r.status === 'error' && <span className="text-red-600 text-[10px]">✗ {r.errors?.[0]}</span>}
                            {r.status === 'warning' && <span className="text-amber-600 text-[10px]">⚠ {r.warnings?.[0]}</span>}
                            {r.status === 'ready' && <span className="text-emerald-600 text-[10px]">✓ Ready</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {pendingItems.length > 0 && (
                <div className="border border-amber-200 rounded overflow-hidden" data-testid="leave-import-pending-section">
                  <div className="bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                    Pending leave on/before {asOnDate} · {pendingItems.length} — tick any already reflected in this sheet's figure (so it isn't deducted twice when later approved)
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {pendingItems.map((p, i) => (
                          <tr key={p.leave_id} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 w-6">
                              <input type="checkbox" checked={includedLeaveIds.has(p.leave_id)}
                                onChange={() => toggleIncluded(p.leave_id)}
                                data-testid={`leave-import-pending-tick-${i}`} />
                            </td>
                            <td className="px-2 py-1.5">{p.employee_name} <span className="text-slate-400 font-mono">{p.emp_id}</span></td>
                            <td className="px-2 py-1.5 font-bold">{p.leave_type}</td>
                            <td className="px-2 py-1.5 font-mono">{p.from_date}{p.to_date !== p.from_date ? ` → ${p.to_date}` : ''}</td>
                            <td className="px-2 py-1.5">{p.days}d</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 2 && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded p-4">
                <Check size={22} className="text-emerald-600 shrink-0" />
                <div>
                  <div className="font-bold text-emerald-800">Import Complete</div>
                  <div className="text-sm text-emerald-700 mt-0.5">{result.inserted} new · {result.replaced} replaced · {result.skipped} skipped</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-between items-center shrink-0">
          {step === 0 && <>
            <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">Cancel</button>
            <button onClick={analyze} disabled={!file || !asOnDate || loading} data-testid="leave-import-analyze"
              className="px-4 py-2 bg-[#3b4872] text-white rounded text-sm font-bold disabled:opacity-40 flex items-center gap-2">
              {loading && <Loader2 size={13} className="animate-spin" />} Analyze →
            </button>
          </>}
          {step === 1 && <>
            <button onClick={() => setStep(0)} className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">← Back</button>
            <button onClick={commit} disabled={ready === 0 || hasErrors || loading} data-testid="leave-import-commit"
              title={hasErrors ? 'Fix error rows first' : undefined}
              className="px-4 py-2 bg-[#3b4872] text-white rounded text-sm font-bold disabled:opacity-40 flex items-center gap-2">
              {loading && <Loader2 size={13} className="animate-spin" />}
              Import {ready} row{ready !== 1 ? 's' : ''}
            </button>
          </>}
          {step === 2 && <>
            <div />
            <button onClick={() => { onDone(); onClose(); }} data-testid="leave-import-done"
              className="px-4 py-2 bg-[#3b4872] text-white rounded text-sm font-bold flex items-center gap-2">
              <Check size={14} /> Done
            </button>
          </>}
        </div>
      </div>
    </div>
  );
}


function EmployeeLedgerDrawer({ emp, year, onClose }) {
  const [data, setData] = useState({ EL: null, PL: null, CO: null });
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('EL');
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [el, pl, co] = await Promise.all([
          apiGet('/hr/leaves/ledger/entries', { employee_id: emp.employee_id, leave_type: 'EL', year }),
          apiGet('/hr/leaves/ledger/entries', { employee_id: emp.employee_id, leave_type: 'PL', year }),
          apiGet('/hr/leaves/ledger/entries', { employee_id: emp.employee_id, leave_type: 'CO', year }),
        ]);
        if (alive) setData({ EL: el, PL: pl, CO: co });
      } catch { /* noop */ } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [emp, year]);
  const cur = data[type];
  const entries = cur?.entries || [];
  const LABELS = {
    conversion_opening: 'Opening (conversion)', opening_balance: 'Opening balance', accrual: 'Monthly accrual',
    manual_credit: 'Manual credit', manual_adjustment: 'Manual adjustment', leave_debit: 'Leave taken',
    leave_release: 'Leave credited back', roster_leave: 'Roster leave', half_day_auto: 'Half-day auto',
    application_hold: 'Application hold', year_end_lapse: 'Year-end lapse', year_end_carry_forward: 'Year-end carry-forward',
    co_earned: 'Comp off earned', co_taken: 'Comp off taken', co_reversal: 'Comp off credited back', brought_forward: 'Brought forward',
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="employee-ledger-drawer">
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-base">{emp.name}</div>
            <div className="text-xs text-slate-500">{emp.emp_id || '—'} · Leave Ledger {year}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="ledger-drawer-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-3">
          <div className="flex gap-2 mb-3">
            {['EL', 'PL', 'CO'].map(t => (
              <button key={t} onClick={() => setType(t)} data-testid={`ledger-drawer-type-${t}`}
                className={`px-3 py-1 text-xs rounded-full border ${type === t ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600'}`}>{t}</button>
            ))}
            <div className="ml-auto text-xs text-slate-500 self-center">
              {cur ? <>Balance: <strong className="text-slate-800">{entries.length ? entries[0].running_balance : 0}</strong> · {cur.count} entries</> : null}
            </div>
          </div>
          {loading ? (
            <div className="text-center py-10 text-slate-400"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm italic">No ledger entries for {type} in {year}.</div>
          ) : (
            <table className="w-full text-xs" data-testid="ledger-drawer-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left py-2 px-2 font-bold text-slate-500 uppercase text-[10px]">Date</th>
                  <th className="text-left py-2 px-2 font-bold text-slate-500 uppercase text-[10px]">Movement</th>
                  <th className="text-right py-2 px-2 font-bold text-emerald-600 uppercase text-[10px]">+Cr</th>
                  <th className="text-right py-2 px-2 font-bold text-red-500 uppercase text-[10px]">−Dr</th>
                  <th className="text-right py-2 px-2 font-bold text-slate-700 uppercase text-[10px]">Balance</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={e.id || i} className="border-b border-slate-100 align-top">
                    <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{String(e.date || e.created_at || '').slice(0, 10)}</td>
                    <td className="py-2 px-2">
                      <div className="font-semibold text-slate-700">{e.leave_type === 'CO' && e.note ? e.note : (LABELS[e.entry_type] || e.entry_type)}</div>
                      {e.reason && e.leave_type !== 'CO' ? <div className="text-[10px] text-slate-400">{e.reason}</div> : null}
                    </td>
                    <td className="py-2 px-2 text-right text-emerald-700">{e.days_credited ? `+${e.days_credited}` : ''}</td>
                    <td className="py-2 px-2 text-right text-red-600">{e.days_debited ? `−${e.days_debited}` : ''}</td>
                    <td className="py-2 px-2 text-right font-bold text-slate-800">{e.affects_balance === false ? '—' : e.running_balance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function LedgerConversionModal({ onClose, onCommitted }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [rulings, setRulings] = useState({}); // "empId:type" -> "writeoff" | "carry"
  const [force, setForce] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/hr/leave-ledger/conversion/report');
      setReport(r);
      setForce(!!r.already_committed);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load report'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const blockers = report?.blockers || {};
  const overUsed = blockers.over_used_unresolved || [];
  const noRecord = blockers.no_balance_record || [];
  const recon = blockers.reconciliation_mismatch || [];
  const diff = report?.difference_check || {};
  const allRuled = overUsed.every(o => rulings[o.key]);
  const hardBlocked = noRecord.length > 0 || recon.length > 0;
  const canCommit = !hardBlocked && allRuled;

  const commit = async () => {
    if (!canCommit) return;
    if (!window.confirm('Commit the leave-ledger conversion?\n\nThis writes the opening + leave-taken entries and switches every EL/PL balance to the ledger. super_admin only.')) return;
    setCommitting(true);
    try {
      const path = `/hr/leave-ledger/conversion/commit${force ? '?force=true' : ''}`;
      const r = await apiPost(path, {
        confirm_token: 'LEAVE-LEDGER-CONVERT',
        over_used_resolutions: rulings,
      });
      const v = r.before_after_check || {};
      toast.success(`Committed — ${v.checked} balances checked, ${v.passed ? 'no differences ✓' : (v.failures?.length || 0) + ' differences'}${r.carried_over_used?.length ? `, ${r.carried_over_used.length} carried` : ''}`);
      onCommitted?.();
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d.message || d.error) : (d || 'Commit failed'));
    }
    setCommitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="ledger-conversion-modal">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-base">Leave Ledger Conversion</div>
            <div className="text-xs text-slate-500">Dry-run report — makes the ledger the single source of truth (super_admin)</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="conversion-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {loading ? (
            <div className="text-center py-10 text-slate-400"><Loader2 size={16} className="inline animate-spin mr-2" />Building report…</div>
          ) : !report ? null : (
            <>
              <div className={`rounded p-3 border text-sm ${report.already_committed ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="conversion-status">
                {report.already_committed
                  ? 'Conversion is LIVE — balances are read from the ledger.'
                  : 'Not yet committed — balances still use the legacy calculation.'}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { k: 'Employees', v: report.employees_scanned },
                  { k: 'Opening entries', v: report.entries_to_write?.opening },
                  { k: 'Leave debits', v: report.entries_to_write?.leave_debit },
                  { k: 'Reconcile diffs', v: (diff.differences?.length ?? 0) },
                ].map(t => (
                  <div key={t.k} className="border rounded p-2 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{t.k}</div>
                    <div className="text-lg font-extrabold text-slate-800">{t.v ?? '—'}</div>
                  </div>
                ))}
              </div>
              <div className={`rounded p-3 border text-sm font-semibold ${diff.passed ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="conversion-diffcheck">
                Difference check: {diff.passed ? 'PASSED ✓ — projected ledger balance equals today\u2019s displayed balance for all reconciled employees' : `FAILED — ${diff.differences?.length || 0} rows differ`}
                <div className="text-xs font-normal mt-1">{diff.checked} employee-balances compared (projected vs legacy displayed)</div>
              </div>

              {/* HARD BLOCKERS */}
              {noRecord.length > 0 && (
                <details className="border rounded border-red-200" open data-testid="conversion-no-record">
                  <summary className="px-3 py-2 text-sm font-semibold cursor-pointer bg-red-50 text-red-800">⛔ No balance record — HR must set opening first · {noRecord.length}</summary>
                  <div className="max-h-48 overflow-y-auto px-3 py-2 text-xs">
                    {noRecord.slice(0, 200).map((r, i) => (
                      <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                        <span>{r.emp_id} · {r.name} · {r.leave_type}</span>
                        <span className="text-slate-500">used {r.used}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {recon.length > 0 && (
                <details className="border rounded border-red-200" open data-testid="conversion-recon">
                  <summary className="px-3 py-2 text-sm font-semibold cursor-pointer bg-red-50 text-red-800">⛔ Reconciliation mismatch — review these ledgers · {recon.length}</summary>
                  <div className="max-h-48 overflow-y-auto px-3 py-2 text-xs">
                    {recon.slice(0, 200).map((r, i) => (
                      <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                        <span>{r.emp_id} · {r.name} · {r.leave_type}</span>
                        <span className="text-slate-500">projected {r.projected} vs displayed {r.displayed_before} (Δ {r.diff})</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* OVER-USED — needs a ruling per employee */}
              {overUsed.length > 0 && (
                <div className="border rounded border-amber-300" data-testid="conversion-overused">
                  <div className="px-3 py-2 text-sm font-semibold bg-amber-50 text-amber-800 flex items-center justify-between">
                    <span>⚠ Over-used — rule each before committing · {overUsed.length}</span>
                    <div className="flex gap-1">
                      <button onClick={() => setRulings(Object.fromEntries(overUsed.map(o => [o.key, 'writeoff'])))}
                        className="text-[10px] px-2 py-0.5 border border-amber-400 rounded" data-testid="overused-all-writeoff">All write-off</button>
                      <button onClick={() => setRulings(Object.fromEntries(overUsed.map(o => [o.key, 'carry'])))}
                        className="text-[10px] px-2 py-0.5 border border-amber-400 rounded" data-testid="overused-all-carry">All carry</button>
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto px-3 py-2 text-xs">
                    {overUsed.map((o) => (
                      <div key={o.key} className="flex items-center justify-between border-b border-slate-100 py-1.5 gap-2">
                        <span className="flex-1">{o.emp_id} · {o.name} · {o.leave_type} <span className="text-slate-400">({o.stored} − {o.used} = {o.raw_remaining})</span></span>
                        <div className="flex gap-1">
                          {['writeoff', 'carry'].map(opt => (
                            <button key={opt} onClick={() => setRulings(prev => ({ ...prev, [o.key]: opt }))}
                              data-testid={`overused-${o.key}-${opt}`}
                              className={`px-2 py-0.5 rounded border text-[10px] ${rulings[o.key] === opt ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600'}`}>
                              {opt === 'writeoff' ? 'Write off → 0' : 'Carry −ve'}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(report.retired_type_approvals?.length || 0) > 0 && (
                <details className="border rounded" data-testid="conversion-retired">
                  <summary className="px-3 py-2 text-sm font-semibold cursor-pointer bg-slate-50">Retired-type approvals (CL/SL/SPL) — not deducted from EL/PL · {report.retired_type_approvals.length}</summary>
                  <div className="max-h-48 overflow-y-auto px-3 py-2 text-xs">
                    {report.retired_type_approvals.slice(0, 200).map((r, i) => (
                      <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                        <span>{r.emp_id} · {r.name}</span>
                        <span className="text-slate-500">{r.leave_type} · {r.from_date} → {r.to_date} · {r.days}d</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex gap-2 justify-end items-center">
          {report && !canCommit && !loading && (
            <span className="text-[11px] text-slate-500 mr-auto">{hardBlocked ? 'Resolve the ⛔ blockers first' : (overUsed.length ? 'Rule every over-used employee first' : '')}</span>
          )}
          <button onClick={load} disabled={loading} className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50" data-testid="conversion-refresh">Refresh dry-run</button>
          <button onClick={commit} disabled={committing || loading || !canCommit}
            className="px-4 py-2 bg-[#3b4872] text-white rounded text-sm font-bold disabled:opacity-40" data-testid="conversion-commit-btn">
            {committing ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
            {report?.already_committed ? 'Re-run (force)' : 'Commit conversion'}
          </button>
        </div>
      </div>
    </div>
  );
}

// M7 · Backdated-leave attendance repair (82-day). Dry-run preview any HR user can
// see; the commit is super_admin + confirm-token guarded (backend-enforced).
function AttendanceRepairModal({ onClose, onCommitted }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [rowActing, setRowActing] = useState(null);      // "<leave_id>|<date>" in flight
  const [keepLwpFor, setKeepLwpFor] = useState(null);    // row being revoked ("Keep LWP")
  const [keepLwpReason, setKeepLwpReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/hr/leaves/attendance-repair/preview', { year });
      setReport(r);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load repair preview'); }
    setLoading(false);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const commit = async () => {
    if (!report?.count) return;
    if (!window.confirm(`Repair ${report.count} leave-day(s) for ${year}?\n\nThis sets each covered day's attendance to Paid Leave by (employee_id, date). It never overwrites a Present punch. super_admin only.`)) return;
    setCommitting(true);
    try {
      const r = await apiPost('/hr/leaves/attendance-repair/commit', {
        confirm_token: 'LEAVE-ATT-REPAIR', year,
      });
      const sk = (r.skipped_present || []).length;
      toast.success(`Repaired ${r.days_repaired} day(s) across ${r.leaves_processed} leave(s)${sk ? ` · ${sk} left (Present punch)` : ''}`);
      onCommitted?.();
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d.message || d.error) : (d || 'Repair failed'));
    }
    setCommitting(false);
  };

  const makeRowPaid = async (row) => {
    setRowActing(`${row.leave_id}|${row.date}`);
    try {
      await apiPost('/hr/leaves/attendance-repair/lwp-row/make-paid', { leave_id: row.leave_id, date: row.date });
      toast.success(`${row.date} set to Paid Leave`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to repair this row');
    }
    setRowActing(null);
  };

  const doKeepLwp = async () => {
    if (!keepLwpFor || !keepLwpReason.trim()) { toast.error('A reason is required to revoke'); return; }
    setRowActing(`${keepLwpFor.leave_id}|${keepLwpFor.date}`);
    try {
      const r = await fetch(`${API}/api/hr/leaves/${keepLwpFor.leave_id}/revoke?reason=${encodeURIComponent(keepLwpReason.trim())}`, {
        method: 'PUT', credentials: 'include',
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Failed to revoke'); }
      toast.success('Leave revoked — kept as LWP');
      setKeepLwpFor(null); setKeepLwpReason('');
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to revoke');
    }
    setRowActing(null);
  };

  const markAllLwp = async () => {
    const pureCount = (report?.lwp_review_rows || []).filter(r => !r.pay_effect).length;
    if (!pureCount) return;
    if (!window.confirm(`Mark ${pureCount} pure-LWP row(s) as LWP for ${year}? No pay effect.`)) return;
    setCommitting(true);
    try {
      const r = await apiPost('/hr/leaves/attendance-repair/lwp-row/mark-all-lwp', { year });
      toast.success(`Marked ${r.updated} day(s) as LWP`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
    setCommitting(false);
  };

  const months = report?.months || [];
  const rows = report?.rows || [];
  const lwpPureRows = (report?.lwp_review_rows || []).filter(r => !r.pay_effect);
  const lwpPayEffectRows = (report?.lwp_review_rows || []).filter(r => r.pay_effect);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="attendance-repair-modal">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-base">Backdated-Leave Attendance Repair (M7)</div>
            <div className="text-xs text-slate-500">Approved paid leaves still paid as LOP (A / LWP / blank). Dry-run — commit is super_admin only.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="repair-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Leave year</span>
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="border border-slate-200 rounded px-2 py-1 text-xs bg-white" data-testid="repair-year">
              {[new Date().getFullYear(), new Date().getFullYear() - 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={load} disabled={loading} className="text-xs px-3 py-1 border border-slate-300 rounded hover:bg-slate-50" data-testid="repair-refresh">Refresh</button>
          </div>

          {loading ? (
            <div className="text-center py-10 text-slate-400"><Loader2 size={16} className="inline animate-spin mr-2" />Building dry-run…</div>
          ) : !report ? null : (report.count === 0 && !report.lwp_review_count) ? (
            <div className="rounded p-4 border bg-emerald-50 border-emerald-200 text-emerald-800 text-sm" data-testid="repair-empty">
              Nothing to repair for {report.leave_year} — every approved paid-leave day is already reflected in attendance. ✓
            </div>
          ) : (
            <>
              {report.count > 0 && (
              <div className="rounded p-3 border bg-amber-50 border-amber-200 text-amber-800 text-sm font-semibold" data-testid="repair-summary">
                {report.count} leave-day(s) in {report.leave_year} are still paid as LOP and will become Paid Leave.
              </div>
              )}

              {report.count > 0 && (
              <div className="border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Month</th>
                      <th className="text-center px-3 py-2">Days</th>
                      <th className="text-left px-3 py-2">Payroll run?</th>
                      <th className="text-left px-3 py-2">Arrear</th>
                    </tr>
                  </thead>
                  <tbody data-testid="repair-months">
                    {months.map(m => (
                      <tr key={m.month} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono">{m.month}</td>
                        <td className="px-3 py-1.5 text-center font-bold">{m.count}</td>
                        <td className="px-3 py-1.5">
                          {m.payroll_run
                            ? <span className="text-rose-700 font-semibold">Yes — already paid</span>
                            : <span className="text-slate-500">No — not yet run</span>}
                        </td>
                        <td className="px-3 py-1.5">
                          {m.arrear_label
                            ? <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-100 text-amber-800 rounded">{m.arrear_label}</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}

              {report.count > 0 && (
              <details className="border rounded" data-testid="repair-rows">
                <summary className="px-3 py-2 text-sm font-semibold cursor-pointer bg-slate-50">Affected days · {rows.length}</summary>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Employee</th>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-left px-3 py-2">Now</th>
                        <th className="text-left px-3 py-2">Pay effect</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, 500).map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-1.5">{r.name} <span className="text-slate-400 font-mono">{r.emp_id}</span></td>
                          <td className="px-3 py-1.5 font-mono">{r.date}</td>
                          <td className="px-3 py-1.5">{r.leave_type}</td>
                          <td className="px-3 py-1.5">{r.current_attendance}</td>
                          <td className="px-3 py-1.5 text-slate-600">{r.pay_effect}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
              )}

              {lwpPayEffectRows.length > 0 && (
                <details className="border rounded" data-testid="repair-lwp-payeffect-rows" open>
                  <summary className="px-3 py-2 text-sm font-semibold cursor-pointer bg-rose-50 text-rose-800">
                    LWP showing on a PAID leave — decide per row (has a pay effect) · {lwpPayEffectRows.length}
                  </summary>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-400 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5">Employee</th>
                          <th className="text-left px-3 py-1.5">Date</th>
                          <th className="text-left px-3 py-1.5">Leave</th>
                          <th className="text-left px-3 py-1.5">Now</th>
                          <th className="text-left px-3 py-1.5">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lwpPayEffectRows.slice(0, 300).map((r, i) => {
                          const key = `${r.leave_id}|${r.date}`;
                          const busy = rowActing === key;
                          return (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-3 py-1.5">{r.name} <span className="text-slate-400 font-mono">{r.emp_id}</span></td>
                              <td className="px-3 py-1.5 font-mono">{r.date}</td>
                              <td className="px-3 py-1.5">{r.leave_type}</td>
                              <td className="px-3 py-1.5">{r.current_attendance}</td>
                              <td className="px-3 py-1.5">
                                <div className="flex gap-1.5">
                                  <button onClick={() => makeRowPaid(r)} disabled={busy}
                                    data-testid={`lwp-row-make-paid-${i}`}
                                    className="px-2 py-1 rounded bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-40">
                                    Make it Paid Leave
                                  </button>
                                  <button onClick={() => { setKeepLwpFor(r); setKeepLwpReason(''); }} disabled={busy}
                                    data-testid={`lwp-row-keep-lwp-${i}`}
                                    className="px-2 py-1 rounded border border-rose-300 text-rose-600 text-[11px] font-semibold hover:bg-rose-50 disabled:opacity-40">
                                    Keep LWP
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              {lwpPureRows.length > 0 && (
                <details className="border rounded" data-testid="repair-lwp-pure-rows">
                  <summary className="px-3 py-2 text-sm font-semibold cursor-pointer bg-slate-50 flex items-center justify-between">
                    <span>LWP with no pay effect (attendance blank/A) · {lwpPureRows.length}</span>
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); markAllLwp(); }} disabled={committing}
                      data-testid="repair-mark-all-lwp-btn"
                      className="px-2.5 py-1 rounded bg-slate-700 text-white text-[11px] font-semibold hover:bg-slate-800 disabled:opacity-40">
                      Mark all as LWP
                    </button>
                  </summary>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-400 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5">Employee</th>
                          <th className="text-left px-3 py-1.5">Date</th>
                          <th className="text-left px-3 py-1.5">Now</th>
                          <th className="text-left px-3 py-1.5">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lwpPureRows.slice(0, 300).map((r, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-3 py-1.5">{r.name} <span className="text-slate-400 font-mono">{r.emp_id}</span></td>
                            <td className="px-3 py-1.5 font-mono">{r.date}</td>
                            <td className="px-3 py-1.5">{r.current_attendance}</td>
                            <td className="px-3 py-1.5 text-slate-500">{r.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </>
          )}
        </div>
        {keepLwpFor && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setKeepLwpFor(null)} data-testid="repair-keep-lwp-modal">
            <div className="bg-white rounded-md w-full max-w-sm p-4 space-y-3" onClick={e => e.stopPropagation()}>
              <div className="font-semibold text-sm text-slate-800">Keep LWP — revoke this leave</div>
              <div className="text-xs text-slate-600">
                Revoking <strong>{keepLwpFor.name}</strong>'s <strong>{keepLwpFor.leave_type}</strong> leave
                on {keepLwpFor.date}. This day stays LWP.
              </div>
              <textarea rows={3} value={keepLwpReason} onChange={e => setKeepLwpReason(e.target.value)}
                placeholder="Why is this approved leave being revoked?"
                className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs" data-testid="repair-keep-lwp-reason-input" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setKeepLwpFor(null)} className="px-3 py-1.5 rounded border border-slate-300 text-xs" data-testid="repair-keep-lwp-cancel">Cancel</button>
                <button onClick={doKeepLwp} disabled={!keepLwpReason.trim() || rowActing}
                  className="px-3 py-1.5 rounded bg-rose-600 text-white text-xs font-semibold disabled:opacity-40" data-testid="repair-keep-lwp-confirm">
                  Revoke &amp; keep LWP
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex gap-2 justify-end items-center">
          <span className="text-[11px] text-slate-500 mr-auto">Commit is super_admin only. Present-punch days are never overwritten.</span>
          <button onClick={commit} disabled={committing || loading || !report?.count}
            className="px-4 py-2 bg-amber-600 text-white rounded text-sm font-bold disabled:opacity-40 flex items-center gap-1.5" data-testid="repair-commit-btn">
            {committing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench size={13} />}
            Commit repair
          </button>
        </div>
      </div>
    </div>
  );
}

// M3-end: read-only report of employee-dates already carrying 2+ leave records
// (approved or pending, retired-type overlaps included). Ellora's review, never auto-fixed.
function DoubleChargedDaysModal({ onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiGet('/hr/leaves/double-charged-days')
      .then(setReport)
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const rows = report?.rows || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="double-charged-days-modal">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-base">Double-Charged Days</div>
            <div className="text-xs text-slate-500">Read-only — existing employee-dates carrying 2+ leave records. Nothing here is changed automatically.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="double-charged-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-4">
          {loading ? (
            <div className="text-center py-10 text-slate-400"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
          ) : rows.length === 0 ? (
            <div className="rounded p-4 border bg-emerald-50 border-emerald-200 text-emerald-800 text-sm" data-testid="double-charged-empty">
              No double-charged days found. ✓
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="double-charged-rows">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Leaves on this day</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-1.5">{r.name} <span className="text-slate-400 font-mono">{r.emp_id}</span></td>
                    <td className="px-3 py-1.5 font-mono">{r.date}</td>
                    <td className="px-3 py-1.5">
                      {r.leaves.map((l, j) => (
                        <span key={j} className="inline-block mr-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                          {l.leave_type} · {l.status}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function LeaveLedger() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  // M8: ONE "Adjust balance" dialog (replaces the old separate additive-credit
  // modal and absolute-set modal) — opened from the balance cell or the Adjust button.
  const [adjustFor, setAdjustFor] = useState(null); // { employee_id, name, leave_type, current }
  const [adjustMode, setAdjustMode] = useState('add'); // 'add' | 'deduct' | 'set'
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  // Import wizard
  const [showImport, setShowImport] = useState(false);
  // M1: per-employee ledger drawer + conversion modal
  const [viewLedgerFor, setViewLedgerFor] = useState(null);
  const [showConversion, setShowConversion] = useState(false);
  // M7: backdated-leave attendance repair (82-day) report + guarded commit
  const [showRepair, setShowRepair] = useState(false);
  // M3-end: double-charged-days read-only report
  const [showDoubleCharged, setShowDoubleCharged] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await apiGet('/hr/leaves/ledger/summary', { year: yearFilter });
      setSummary(s);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [yearFilter]);

  useEffect(() => { load(); }, [load]);

  // Cell-level balance update without full reload
  const patchCell = (employee_id, leave_type, new_balance) => {
    setSummary(prev => {
      if (!prev) return prev;
      const key = leave_type === 'EL' ? 'el_balance' : leave_type === 'PL' ? 'pl_balance' : 'co_balance';
      return {
        ...prev,
        employees: prev.employees.map(e =>
          e.employee_id === employee_id ? { ...e, [key]: new_balance } : e
        ),
      };
    });
  };

  const saveAdjust = async () => {
    if (!adjustFor || adjustAmount === '' || !adjustReason.trim()) return;
    const amount = parseFloat(adjustAmount);
    if (isNaN(amount) || amount < 0 || (adjustMode !== 'set' && amount <= 0)) {
      toast.error('Enter a valid amount'); return;
    }
    setAdjustSaving(true);
    try {
      const res = await apiPut('/hr/leave-ledger/balance', {
        employee_id: adjustFor.employee_id,
        leave_type: adjustFor.leave_type,
        mode: adjustMode,
        amount,
        reason: adjustReason.trim(),
      });
      toast.success(`${adjustFor.leave_type} balance ${res.old_balance} → ${res.new_balance} for ${adjustFor.name}`);
      patchCell(adjustFor.employee_id, adjustFor.leave_type, res.new_balance);
      setAdjustFor(null); setAdjustAmount(''); setAdjustReason('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to adjust balance'); }
    setAdjustSaving(false);
  };

  const employees = summary?.employees || [];
  const totals = summary?.totals || {};

  const openAdjust = (emp, lt, current, mode = 'add') => {
    setAdjustFor({ employee_id: emp.employee_id, name: emp.name, leave_type: lt, current });
    setAdjustMode(mode);
    setAdjustAmount('');
    setAdjustReason('');
  };

  return (
    <Card data-testid="leave-ledger">
      {showImport && (
        <LeaveBalanceImportWizard
          onClose={() => setShowImport(false)}
          onDone={load}
        />
      )}
      {viewLedgerFor && (
        <EmployeeLedgerDrawer emp={viewLedgerFor} year={yearFilter} onClose={() => setViewLedgerFor(null)} />
      )}
      {showConversion && (
        <LedgerConversionModal onClose={() => setShowConversion(false)} onCommitted={load} />
      )}
      {showRepair && (
        <AttendanceRepairModal onClose={() => setShowRepair(false)} onCommitted={load} />
      )}
      {showDoubleCharged && (
        <DoubleChargedDaysModal onClose={() => setShowDoubleCharged(false)} />
      )}
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">
            Leave Ledger — {yearFilter}
          </CardTitle>
          <div className="flex items-center gap-2">
            <select value={yearFilter} onChange={e => setYearFilter(Number(e.target.value))}
              className="border border-slate-200 rounded px-2 py-1 text-xs bg-white">
              {[new Date().getFullYear(), new Date().getFullYear() - 1].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              onClick={() => setShowConversion(true)}
              data-testid="ledger-conversion-btn"
              className="text-xs px-3 py-1.5 rounded-md border border-emerald-600 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5"
            >
              <FileSpreadsheet size={12} /> Ledger Conversion
            </button>
            <button
              onClick={() => setShowRepair(true)}
              data-testid="attendance-repair-btn"
              className="text-xs px-3 py-1.5 rounded-md border border-amber-600 text-amber-700 hover:bg-amber-50 flex items-center gap-1.5"
            >
              <Wrench size={12} /> Attendance Repair
            </button>
            <button
              onClick={() => setShowDoubleCharged(true)}
              data-testid="double-charged-days-btn"
              className="text-xs px-3 py-1.5 rounded-md border border-rose-500 text-rose-600 hover:bg-rose-50 flex items-center gap-1.5"
            >
              <AlertTriangle size={12} /> Double-Charged Days
            </button>
            <button
              onClick={() => setShowImport(true)}
              data-testid="leave-import-btn"
              className="text-xs px-3 py-1.5 rounded-md border border-[#3b4872] text-[#3b4872] hover:bg-indigo-50 flex items-center gap-1.5"
            >
              <Upload size={12} /> Import Opening Balances
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* KPI tiles */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="border rounded p-3 bg-emerald-50 border-emerald-200">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">Total EL Balance</div>
            <div className="text-2xl font-extrabold mt-1 text-emerald-700">{totals.el ?? 0}<span className="text-xs ml-1 font-normal text-emerald-500">days</span></div>
          </div>
          <div className="border rounded p-3 bg-violet-50 border-violet-200">
            <div className="text-[10px] uppercase tracking-widest font-bold text-violet-600">Total PL Balance</div>
            <div className="text-2xl font-extrabold mt-1 text-violet-700">{totals.pl ?? 0}<span className="text-xs ml-1 font-normal text-violet-500">days</span></div>
          </div>
          <div className="border rounded p-3 bg-amber-50 border-amber-200">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600">Total CO Balance</div>
            <div className="text-2xl font-extrabold mt-1 text-amber-700">{totals.co ?? 0}<span className="text-xs ml-1 font-normal text-amber-500">days</span></div>
          </div>
        </div>
        <div className="text-xs text-slate-400 mb-3">Click a balance cell, or use Adjust, to add, deduct, or set it — one dialog, one reason, one ledger entry.</div>

        {loading ? (
          <div className="text-center py-8 text-slate-400"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
        ) : employees.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm italic">No active employees found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Emp Code</th>
                  <th className="text-left py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Name</th>
                  <th className="text-left py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Dept</th>
                  <th className="text-center py-2 px-2 font-bold text-emerald-600 uppercase tracking-wider text-[10px]">EL</th>
                  <th className="text-center py-2 px-2 font-bold text-violet-600 uppercase tracking-wider text-[10px]">PL</th>
                  <th className="text-center py-2 px-2 font-bold text-amber-600 uppercase tracking-wider text-[10px]">CO</th>
                  <th className="text-center py-2 px-2 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Adjust</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, i) => {
                  const cols = [
                    { lt: 'EL', val: emp.el_balance, color: 'emerald' },
                    { lt: 'PL', val: emp.pl_balance, color: 'violet' },
                    { lt: 'CO', val: emp.co_balance, color: 'amber' },
                  ];
                  return (
                    <tr key={emp.employee_id} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 ? 'bg-slate-50/30' : ''}`}>
                      <td className="py-2 px-2 font-mono text-slate-600">{emp.emp_id || '—'}</td>
                      <td className="py-2 px-2 font-semibold">
                        <button onClick={() => setViewLedgerFor(emp)} data-testid={`leave-view-ledger-${emp.emp_id}`}
                          className="text-[#3b4872] hover:underline text-left">{emp.name}</button>
                      </td>
                      <td className="py-2 px-2 text-slate-500">{emp.department || '—'}</td>
                      {cols.map(({ lt, val, color }) => (
                        <td key={lt} className="py-2 px-2 text-center">
                          <button
                            onClick={() => openAdjust(emp, lt, val, 'set')}
                            title={`Click to adjust ${lt} balance`}
                            data-testid={`leave-cell-${lt}-${emp.emp_id}`}
                            className={`font-bold text-${color}-700 hover:bg-${color}-100 rounded px-1.5 py-0.5 transition-colors cursor-pointer`}
                          >{val}</button>
                        </td>
                      ))}
                      <td className="py-2 px-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {cols.map(({ lt, val }) => (
                            <button key={lt}
                              onClick={() => openAdjust(emp, lt, val, 'add')}
                              className="px-1.5 py-0.5 text-[9px] border border-slate-200 rounded hover:bg-slate-100 font-bold text-slate-600"
                              title={`Adjust ${lt} balance for ${emp.name}`}
                              data-testid={`leave-adjust-${lt}-${emp.emp_id}`}>
                              Adjust {lt}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* M8: ONE Adjust Balance dialog — add / deduct / set, mandatory reason */}
        {adjustFor && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={() => setAdjustFor(null)}>
            <div className="bg-white rounded-md p-5 w-full max-w-sm shadow-xl"
              onClick={e => e.stopPropagation()} data-testid="leave-adjust-modal">
              <h3 className="font-display font-bold text-base mb-1">Adjust {adjustFor.leave_type} Balance</h3>
              <p className="text-xs text-slate-500 mb-3">{adjustFor.name} · Current: <strong>{adjustFor.current}</strong> days</p>

              <div className="mb-3 flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="leave-adjust-mode-tabs">
                {[
                  { key: 'add', label: 'Add' },
                  { key: 'deduct', label: 'Deduct' },
                  { key: 'set', label: 'Set to' },
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => setAdjustMode(key)}
                    data-testid={`leave-adjust-mode-${key}`}
                    className={`flex-1 py-1.5 font-bold ${adjustMode === key ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="mb-1 text-[10px] text-slate-400 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                {adjustMode === 'set'
                  ? 'Replaces the balance entirely.'
                  : `${adjustMode === 'add' ? 'Adds' : 'Deducts'} days on top of the current balance.`} Writes one ledger entry for the difference.
              </div>

              <div className="mt-3 mb-3">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                  {adjustMode === 'set' ? 'New Balance (days) *' : 'Days *'}
                </label>
                <input type="number" min="0" step="0.5" value={adjustAmount}
                  onChange={e => setAdjustAmount(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  placeholder="e.g. 1, 3.5" autoFocus data-testid="leave-adjust-amount" />
              </div>
              <div className="mb-4">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Reason * <span className="text-red-500">(required — audit trail)</span></label>
                <input value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  placeholder="e.g. Opening balance, correction, carry-forward" data-testid="leave-adjust-reason" />
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setAdjustFor(null)}
                  className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-50">Cancel</button>
                <button onClick={saveAdjust}
                  disabled={adjustSaving || adjustAmount === '' || !adjustReason.trim()}
                  className="px-4 py-2 bg-[#3b4872] text-white rounded text-sm font-bold disabled:opacity-40"
                  data-testid="leave-adjust-save-btn">
                  {adjustSaving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                  {adjustMode === 'set' ? 'Set Balance' : adjustMode === 'add' ? 'Add Days' : 'Deduct Days'}
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AttendanceMatrix() {
  const { user: currentUser } = useAuth() || {};
  const canEdit = (() => {
    const role = (currentUser?.role || '').toLowerCase();
    const dept = (currentUser?.department || '').toUpperCase();
    return role === 'super_admin' || role === 'hr' || role === 'hr_manager' || dept === 'HR';
  })();
  const [month, setMonth] = useState(formatYM(new Date()));
  const [data, setData] = useState(null);
  const [balances, setBalances] = useState({}); // employee_id → { el_balance, pl_balance, co_balance }
  const [loading, setLoading] = useState(false);
  const [picker, setPicker] = useState(null); // { empId, date, anchorRect }
  const [ioBusy, setIoBusy] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  // Attendance filters: by Department, Project, Work Location
  const [fDept, setFDept] = useState('');
  const [fProj, setFProj] = useState('');
  const [fLoc, setFLoc] = useState('');
  const [fQuery, setFQuery] = useState('');
  const [attPage, setAttPage] = useState(1);
  const [attPageSize, setAttPageSize] = useState(50);
  const [deptOpts, setDeptOpts] = useState([]);
  const [projOpts, setProjOpts] = useState([]);
  // M3: pending-application banner + roster-reversal confirm modal
  const [pendingBanner, setPendingBanner] = useState(null); // { empId, empName, date, detail }
  const [reversalConfirm, setReversalConfirm] = useState(null); // { empId, date, status, projectId, detail }
  const [decidingPending, setDecidingPending] = useState(false);
  useEffect(() => {
    apiGet('/user-management/departments').then(r => setDeptOpts(r?.departments || [])).catch(() => {});
    apiGet('/hr/assignable-projects').then(d => setProjOpts(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  useEffect(() => { setAttPage(1); }, [fDept, fProj, fQuery, month]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet('/hr/attendance', {
        month, page: attPage, limit: attPageSize,
        department: fDept || undefined, project_id: fProj || undefined, search: fQuery || undefined,
      });
      setData(d);
      apiGet('/hr/leaves/ledger/summary', { year: Number(month.slice(0, 4)) })
        .then(s => setBalances(Object.fromEntries((s.employees || []).map(e => [e.employee_id, e]))))
        .catch(() => {});
    } finally {
      setLoading(false);
    }
  }, [month, attPage, attPageSize, fDept, fProj, fQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shift = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(formatYM(d));
  };

  const SUMMARY_BUCKET = (st) => {
    if (!st) return null;
    if (st === 'P' || st === 'A' || st === 'H') return st;
    if (['L', 'PL', 'SL', 'CL', 'SPL', 'ML', 'LWP'].includes(st)) return 'TOTAL_LEAVE';
    return null;
  };

  const applyLocalCellUpdate = (empId, date, newStatus) => {
    setData(prev => {
      if (!prev) return prev;
      const oldStatus = prev.matrix?.[empId]?.[date];
      const newMatrix = { ...(prev.matrix || {}), [empId]: { ...(prev.matrix?.[empId] || {}) } };
      if (newStatus === null) {
        delete newMatrix[empId][date];
      } else {
        newMatrix[empId][date] = newStatus;
      }
      // Update row summary
      const oldBucket = SUMMARY_BUCKET(oldStatus);
      const newBucket = SUMMARY_BUCKET(newStatus);
      const rowSummary = { ...(prev.summary?.[empId] || {}) };
      if (oldBucket && oldBucket !== newBucket) {
        rowSummary[oldBucket] = Math.max(0, (rowSummary[oldBucket] || 0) - 1);
      }
      if (newBucket && newBucket !== oldBucket) {
        rowSummary[newBucket] = (rowSummary[newBucket] || 0) + 1;
      }
      return {
        ...prev,
        matrix: newMatrix,
        summary: { ...(prev.summary || {}), [empId]: rowSummary },
      };
    });
  };

  const markCell = async (empId, date, status, projectId, opts = {}) => {
    // Optimistic: update UI immediately, rollback on failure
    const prevStatus = data?.matrix?.[empId]?.[date];
    applyLocalCellUpdate(empId, date, status);
    setPicker(null);
    try {
      const body = { employee_id: empId, date, status };
      if (projectId) body.project_id = projectId;
      if (opts.confirmReversal) body.confirm_reversal = true;
      const r = await fetch(`${API}/api/hr/attendance/mark`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
        const detail = e.detail;
        if (detail && typeof detail === 'object') {
          if (detail.code === 'confirm_reversal_required') {
            setReversalConfirm({ empId, date, status, projectId, detail });
            return;
          }
          if (detail.code === 'application_pending') {
            const empName = (data?.employees || []).find(x => x.id === empId)?.name;
            setPendingBanner({ empId, empName, date, detail });
            return;
          }
          toast.error(detail.message || 'Failed to mark');
          return;
        }
        toast.error(detail || 'Failed to mark');
        return;
      }
      const out = await r.json().catch(() => ({}));
      let msg = `${STATUS_LABELS[status]} marked`;
      if (out.reversed_leave) msg += ` · ${out.reversed_leave.days} ${out.reversed_leave.leave_type} returned to balance`;
      if (out.leave_record_created) msg += ' · leave record created';
      toast.success(msg);
    } catch {
      toast.error('Network error');
      applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
    }
  };

  const confirmReversalAndMark = async () => {
    if (!reversalConfirm) return;
    const { empId, date, status, projectId } = reversalConfirm;
    setReversalConfirm(null);
    await markCell(empId, date, status, projectId, { confirmReversal: true });
  };

  const decidePendingFromBanner = async (action) => {
    if (!pendingBanner) return;
    setDecidingPending(true);
    try {
      const r = await fetch(`${API}/api/hr/leaves/${pendingBanner.detail.leave_id}/${action}`, {
        method: 'PUT', credentials: 'include',
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || `Failed to ${action}`);
      } else {
        toast.success(`Leave ${action === 'approve' ? 'approved' : 'rejected'}`);
        setPendingBanner(null);
        fetchData();
      }
    } catch {
      toast.error('Network error');
    } finally {
      setDecidingPending(false);
    }
  };

  const clearCell = async (empId, date) => {
    const prevStatus = data?.matrix?.[empId]?.[date];
    applyLocalCellUpdate(empId, date, null);
    setPicker(null);
    try {
      const r = await fetch(
        `${API}/api/hr/attendance/clear?employee_id=${encodeURIComponent(empId)}&date=${encodeURIComponent(date)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to clear');
        applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
        return;
      }
      const out = await r.json().catch(() => ({}));
      toast.success(
        out.leave_records_removed
          ? `Attendance cleared · ${out.leave_records_removed} leave record(s) reverted`
          : 'Attendance cleared'
      );
    } catch {
      toast.error('Network error');
      applyLocalCellUpdate(empId, date, prevStatus ?? null);  // rollback
    }
  };

  // department / project / search are applied server-side (paged roster); location stays client-side
  const allEmps = data?.employees || [];
  const attTotal = Number(data?.total ?? allEmps.length);
  const depts = deptOpts.length ? deptOpts : Array.from(new Set(allEmps.map(e => e.department).filter(Boolean))).sort();
  const projs = projOpts.length ? projOpts : Array.from(
    new Map(allEmps.filter(e => e.project_id).map(e => [e.project_id, { id: e.project_id, name: e.project_name || e.project_id }])).values()
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const projNameById = React.useMemo(
    () => Object.fromEntries(projs.map(p => [p.id, p.name])),
    [projs]
  );
  const officeLocs = (data?.office_locations || []).sort((a, b) => a.name.localeCompare(b.name));
  const filteredEmps = allEmps.filter(e => !(fLoc && (e.office_location_name || '') !== fLoc));
  const clearFilters = () => { setFDept(''); setFProj(''); setFLoc(''); setFQuery(''); };

  // ── Excel import / export / template / auto weekly-off ──────────────────
  const downloadXlsx = async (path, filename) => {
    setIoBusy(true);
    try {
      const r = await fetch(`${API}${path}`, { credentials: 'include' });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `Download failed (${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Download failed');
    } finally {
      setIoBusy(false);
    }
  };

  const downloadTemplate = () => downloadXlsx(`/api/hr/attendance/template?month=${month}`, `attendance-template-${month}.xlsx`);
  const exportXlsx = () => downloadXlsx(`/api/hr/attendance/export?month=${month}`, `attendance-${month}.xlsx`);

  const [autoActionModal, setAutoActionModal] = useState(null); // { projectId, date, preview, busy } | null — Auto-Mark Today only; weekly-off now runs itself every Tuesday
  const [showPunchTimes, setShowPunchTimes] = useState(false); // G3: in/out times under each mark
  const [futureLeaveConfirm, setFutureLeaveConfirm] = useState(null); // G3: confirm before marking a future-dated leave code

  // AM3: today's attendance is decided automatically the next morning — the
  // manual button only ever targets a PAST date (max = yesterday, IST).
  const istYesterdayStr = () => new Date(Date.now() + 5.5 * 3600 * 1000 - 86400000).toISOString().slice(0, 10);

  const openAutoAction = () => setAutoActionModal({ projectId: '', date: istYesterdayStr(), preview: null, busy: false });

  const previewAutoAction = async () => {
    setAutoActionModal((m) => ({ ...m, busy: true }));
    try {
      const url = `${API}/api/hr/attendance/auto-mark-today?dry_run=true&date_str=${autoActionModal.date}${autoActionModal.projectId ? `&project_id=${autoActionModal.projectId}` : ''}`;
      const r = await fetch(url, { method: 'POST', credentials: 'include' });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'Preview failed');
      setAutoActionModal((m) => ({ ...m, preview: out, busy: false }));
    } catch (err) {
      toast.error(err.message || 'Preview failed');
      setAutoActionModal((m) => ({ ...m, busy: false }));
    }
  };

  const confirmAutoAction = async () => {
    setAutoActionModal((m) => ({ ...m, busy: true }));
    try {
      const url = `${API}/api/hr/attendance/auto-mark-today?date_str=${autoActionModal.date}${autoActionModal.projectId ? `&project_id=${autoActionModal.projectId}` : ''}`;
      const r = await fetch(url, { method: 'POST', credentials: 'include' });
      const out = await r.json();
      if (!r.ok) throw new Error(out.detail || 'Failed');
      toast.success(`Auto-marked: ${out.marked_P} P · ${out.marked_H} H · ${out.marked_A} A (${out.skipped_manual} manual kept)`);
      setAutoActionModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Action failed');
      setAutoActionModal((m) => ({ ...m, busy: false }));
    }
  };

  const onWizardImportSuccess = useCallback((result) => {
    // Refresh the roster if the imported month is the one currently displayed
    if (result.month === month) fetchData();
  }, [month, fetchData]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-slate-500">
            Attendance Roster
          </CardTitle>
          <div className="flex items-center gap-2">
            <button onClick={() => shift(-1)} className="p-1.5 border rounded-md hover:bg-slate-50" data-testid="att-prev"><ChevronLeft size={14} /></button>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="att-month"
              className="border border-slate-200 rounded-md px-3 py-1.5 text-sm"
            />
            <button onClick={() => shift(1)} className="p-1.5 border rounded-md hover:bg-slate-50" data-testid="att-next"><ChevronRight size={14} /></button>
            <button
              onClick={downloadTemplate}
              disabled={ioBusy}
              data-testid="att-download-template"
              className="ml-2 text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50"
              title={`Download a blank Excel template for ${month} (employees × days). The month is stamped into the sheet's top row.`}
            >
              <FileSpreadsheet size={12} /> Template
            </button>
            <button
              onClick={() => setShowImportWizard(true)}
              disabled={ioBusy}
              data-testid="att-import-xlsx"
              className="text-xs px-3 py-1.5 rounded-md border border-[#3b4872] text-[#3b4872] hover:bg-indigo-50 flex items-center gap-1.5 disabled:opacity-50"
              title="Import attendance from an Excel file — opens the guided import wizard with preview and error visibility."
            >
              <Upload size={12} /> Import
            </button>
            <button
              onClick={exportXlsx}
              disabled={ioBusy}
              data-testid="att-export-xlsx"
              className="text-xs px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5 disabled:opacity-50"
              title="Export this month's attendance to Excel"
            >
              <Download size={12} /> Export
            </button>
            {canEdit && (
              <button
                onClick={() => openAutoAction()}
                className="text-xs px-3 py-1.5 rounded-md border border-[#3b4872] text-[#3b4872] hover:bg-indigo-50 flex items-center gap-1.5"
                title="Auto-mark P/H/A for a past date based on punch records. Manual marks, HO and WO are preserved. Today is decided automatically the next morning."
                data-testid="auto-mark-attendance-btn"
              >
                ⚡ Auto-Mark
              </button>
            )}
          </div>
        </div>
        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2 pt-3" data-testid="att-filter-row">
          <input
            type="text"
            value={fQuery}
            onChange={(e) => setFQuery(e.target.value)}
            placeholder="Search name / emp-id / designation…"
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs w-56 focus:outline-none focus:border-[#3b4872]"
            data-testid="att-filter-search"
          />
          <select
            value={fDept}
            onChange={(e) => setFDept(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
            data-testid="att-filter-department"
          >
            <option value="">All Departments ({depts.length})</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            value={fProj}
            onChange={(e) => setFProj(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white max-w-[260px]"
            data-testid="att-filter-project"
          >
            <option value="">All Projects ({projs.length})</option>
            {projs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select
            value={fLoc}
            onChange={(e) => setFLoc(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
            data-testid="att-filter-location"
          >
            <option value="">Office Locations ({officeLocs.length})</option>
            {officeLocs.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
          </select>
          <button
            onClick={() => setShowPunchTimes((v) => !v)}
            data-testid="att-toggle-punch-times"
            className={`text-[11px] px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 ${showPunchTimes ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
            title="Show in/out punch times under each mark"
          >
            <Clock size={12} /> Times
          </button>
          <button
            onClick={clearFilters}
            data-testid="att-filter-clear"
            className="text-[11px] text-slate-500 hover:text-rose-600 underline ml-1"
          >
            Clear filters
          </button>
          <span className="text-[11px] text-slate-500 ml-auto" data-testid="att-filter-count">
            <span className="font-semibold text-slate-700">{filteredEmps.length}</span> of {attTotal} employees
          </span>
        </div>
        <Legend />
      </CardHeader>
      {pendingBanner && (
        <div className="mx-4 mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex flex-wrap items-center gap-2 text-xs text-amber-900" data-testid="attendance-pending-application-banner">
          <span className="flex-1 min-w-[220px]">
            <strong>{pendingBanner.empName || 'This employee'}</strong> has a pending{' '}
            <strong>{pendingBanner.detail.leave_type}</strong> application covering {pendingBanner.date} —
            approve or reject it before marking the roster.
          </span>
          <button
            onClick={() => decidePendingFromBanner('approve')}
            disabled={decidingPending}
            data-testid="attendance-pending-banner-approve"
            className="px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >Approve</button>
          <button
            onClick={() => decidePendingFromBanner('reject')}
            disabled={decidingPending}
            data-testid="attendance-pending-banner-reject"
            className="px-2.5 py-1 rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
          >Reject</button>
          <button onClick={() => setPendingBanner(null)} data-testid="attendance-pending-banner-close" className="text-amber-700 hover:text-amber-900"><X size={14} /></button>
        </div>
      )}
      {reversalConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="attendance-reversal-confirm-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-4">
            <h3 className="font-semibold text-slate-800 text-sm">Reverse roster leave?</h3>
            <p className="text-sm text-slate-600">{reversalConfirm.detail.message}</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setReversalConfirm(null)}
                data-testid="attendance-reversal-cancel"
                className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 text-sm hover:bg-slate-50"
              >Cancel</button>
              <button
                onClick={confirmReversalAndMark}
                data-testid="attendance-reversal-confirm"
                className="px-3 py-1.5 rounded-md bg-[#3b4872] text-white text-sm hover:bg-[#2f3a5c]"
              >Confirm</button>
            </div>
          </div>
        </div>
      )}
      <CardContent className="p-0">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        ) : !data || data.employees.length === 0 ? (
          <div className="text-center py-12 text-slate-500">No employees.</div>
        ) : filteredEmps.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No employees match the current filters.
            <button onClick={clearFilters} className="ml-2 text-[#3b4872] underline">Clear filters</button>
          </div>
        ) : (
          <div
            className="overflow-x-auto overflow-y-auto rounded-sm border border-slate-200"
            style={{ maxHeight: 'calc(100vh - 280px)' }}
            data-testid="roster-scroll-container"
          >
            <table className="text-xs border-collapse min-w-max">
              <thead className="bg-slate-50 sticky top-0 z-20">
                <tr>
                  <th className="text-left px-3 py-2 font-display uppercase tracking-[0.14em] text-[10px] text-slate-500 sticky left-0 top-0 bg-slate-50 z-30 min-w-[180px] border-r border-slate-200">Employee</th>
                  {data.days.map(d => {
                    const dt = new Date(d);
                    const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                    return (
                      <th key={d} className={`px-1 py-2 font-mono text-[10px] text-center min-w-[26px] ${isWeekend ? 'text-slate-400' : 'text-slate-600'}`}>
                        {String(dt.getDate()).padStart(2, '0')}
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 border-l border-slate-200" title="Present days">P</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-red-500 bg-red-50" title="Absent days">A</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-amber-600 bg-amber-50" title="Half Day">HD</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-violet-600 bg-violet-50" title="Earned Leave consumed this month">EL</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-violet-700 bg-violet-50" title="Privilege Leave consumed this month">PL</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-fuchsia-700 bg-fuchsia-50 whitespace-nowrap" title="Comp-Off consumed this month">C-Off</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-zinc-700 bg-zinc-100" title="Leave Without Pay (LWP)">LWP</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-slate-500 bg-slate-100 whitespace-nowrap" title="Weekly Off days (WO only)">W/O</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-sky-700 bg-sky-50 whitespace-nowrap" title="Public holidays (HO)">Hol</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-[#3b4872] bg-indigo-50 whitespace-nowrap font-extrabold" title="Total Payable Days = P + HD×0.5 + W/O + Holidays + Paid Leaves">TPD</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-amber-700 bg-amber-50 whitespace-nowrap" title="Weekly-off days actually worked (P / HD / WFH on a weekly off)">Sundays Worked</th>
                  <th className="px-2 py-2 text-center text-[9px] font-display font-bold uppercase tracking-wider text-teal-700 bg-teal-50 whitespace-nowrap" title="Short leaves used this month (6h-<8h days marked Present) — capped at 2/month">SL</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmps.map(emp => (
                  <MatrixRow
                    key={emp.id}
                    emp={emp}
                    days={data.days}
                    rowData={data.matrix?.[emp.id]}
                    summary={data.summary[emp.id]}
                    shortLeaveDates={data.short_leave_days?.[emp.id]}
                    balance={balances[emp.id]}
                    punchTimes={data.punch_times?.[emp.id]}
                    compOff={data.comp_off_days?.[emp.id]}
                    offDayKind={data.off_day_kind?.[emp.id]}
                    showPunchTimes={showPunchTimes}
                    onPick={(date, rect) => setPicker({ empId: emp.id, date, rect })}
                  />
                ))}
              </tbody>
            </table>
            <div className="border-t border-slate-200 px-3 py-2">
              <Pagination page={attPage} pageSize={attPageSize} total={attTotal}
                start={(attPage - 1) * attPageSize} end={Math.min(attPage * attPageSize, attTotal)}
                onPageChange={setAttPage} onPageSizeChange={(n) => { setAttPageSize(n); setAttPage(1); }}
                label="employees" compact data-testid="attendance-pagination" />
            </div>
          </div>
        )}
        {picker && (
          <StatusPicker
            employee={data.employees.find(e => e.id === picker.empId)}
            projNameById={projNameById}
            date={picker.date}
            rect={picker.rect}
            current={data.matrix?.[picker.empId]?.[picker.date]}
            punchTimes={data.punch_times?.[picker.empId]?.[picker.date] || null}
            onPick={(st, pid) => {
              const FUTURE_LEAVE_CODES = ['EL', 'PL', 'CO', 'SL', 'CL', 'ML', 'SPL', 'LWP'];
              const todayStr = new Date().toISOString().slice(0, 10);
              if (picker.date > todayStr && FUTURE_LEAVE_CODES.includes(st)) {
                setFutureLeaveConfirm({ empId: picker.empId, date: picker.date, status: st, projectId: pid });
                return;
              }
              markCell(picker.empId, picker.date, st, pid);
            }}
            onClear={() => clearCell(picker.empId, picker.date)}
            onClose={() => setPicker(null)}
          />
        )}
      </CardContent>
      {showImportWizard && (
        <AttendanceImportWizard
          isOpen={showImportWizard}
          onClose={() => setShowImportWizard(false)}
          currentMonth={month}
          onImportSuccess={onWizardImportSuccess}
        />
      )}
      {futureLeaveConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setFutureLeaveConfirm(null)}>
          <div className="bg-white w-full max-w-sm rounded-md" onClick={(e) => e.stopPropagation()} data-testid="future-leave-confirm-modal">
            <div className="p-4 space-y-2">
              <h3 className="font-display font-bold text-base">Mark a future-dated leave?</h3>
              <p className="text-sm text-slate-700">
                <strong>{STATUS_LABELS[futureLeaveConfirm.status]}</strong> on <strong>{futureLeaveConfirm.date}</strong> — this date hasn't happened yet.
                Confirm this is an approved advance leave, not a mistake.
              </p>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setFutureLeaveConfirm(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-md font-display" data-testid="future-leave-cancel-btn">Cancel</button>
              <button
                onClick={() => { const f = futureLeaveConfirm; setFutureLeaveConfirm(null); markCell(f.empId, f.date, f.status, f.projectId); }}
                className="px-5 py-2 text-sm bg-[#3b4872] text-white rounded-md font-display font-semibold"
                data-testid="future-leave-confirm-btn"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {autoActionModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setAutoActionModal(null)}>
          <div className="bg-white w-full max-w-md rounded-md" onClick={(e) => e.stopPropagation()} data-testid="auto-action-modal">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-display font-bold text-base">
                Auto-Mark Attendance
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Auto-marks P/H/A for all staff for a past date, based on that day's punches. Manual marks, HO and WO are never overwritten. Today is decided automatically the next morning.
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Date</label>
                <input
                  type="date"
                  value={autoActionModal.date}
                  max={istYesterdayStr()}
                  onChange={(e) => setAutoActionModal((m) => ({ ...m, date: e.target.value, preview: null }))}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="auto-action-date-input"
                />
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Scope</label>
                <select
                  value={autoActionModal.projectId}
                  onChange={(e) => setAutoActionModal((m) => ({ ...m, projectId: e.target.value, preview: null }))}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="auto-action-project-select"
                >
                  <option value="">All Projects ({projs.length})</option>
                  {projs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {!autoActionModal.preview ? (
                <button
                  onClick={previewAutoAction}
                  disabled={autoActionModal.busy}
                  data-testid="auto-action-preview-btn"
                  className="w-full py-2.5 border border-[#3b4872] text-[#3b4872] rounded-md text-sm font-display font-bold disabled:opacity-50"
                >
                  {autoActionModal.busy ? 'Loading preview…' : 'Preview'}
                </button>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm" data-testid="auto-action-preview-result">
                  <div><b>{autoActionModal.preview.marked_P}</b> P · <b>{autoActionModal.preview.marked_H}</b> H · <b>{autoActionModal.preview.marked_A}</b> A would be marked</div>
                  <div className="text-xs text-slate-500 mt-1">{autoActionModal.preview.skipped_manual} already manually marked (kept) · {autoActionModal.preview.total} employees in scope</div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setAutoActionModal(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-md font-display" data-testid="auto-action-cancel-btn">Cancel</button>
              <button
                onClick={confirmAutoAction}
                disabled={!autoActionModal.preview || autoActionModal.busy}
                data-testid="auto-action-confirm-btn"
                className="px-5 py-2 text-sm bg-[#3b4872] text-white rounded-md font-display font-semibold disabled:opacity-50"
              >
                {autoActionModal.busy ? 'Applying…' : 'Confirm & Mark'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

const MatrixRow = React.memo(function MatrixRow({ emp, days, rowData, summary, shortLeaveDates, balance, punchTimes, compOff, offDayKind, showPunchTimes, onPick }) {
  const s = summary || {};
  const slSet = new Set(shortLeaveDates || []);
  const fmtBal = (v) => (v == null ? '—' : Number(v).toFixed(Number(v) % 1 === 0 ? 0 : 1));
  return (
    <tr className="border-b border-slate-100 hover:bg-indigo-50/30" data-testid={`att-row-${emp.emp_id}`}>
      <td className="px-3 py-1.5 sticky left-0 bg-white z-10 hover:bg-indigo-50/30 border-r border-slate-200">
        <div className="font-medium text-slate-800">{emp.name}</div>
        <div className="text-[10px] text-slate-500">{emp.emp_id} · {emp.designation || '—'}</div>
        {balance && (
          <div className="text-[9px] font-mono text-slate-500 whitespace-nowrap" data-testid={`att-bal-${emp.emp_id}`} title="Live leave balance (EL · PL · C-Off)">
            <span className="text-violet-600">EL {fmtBal(balance.el_balance)}</span>
            <span className="mx-1 text-slate-300">·</span>
            <span className="text-violet-700">PL {fmtBal(balance.pl_balance)}</span>
            <span className="mx-1 text-slate-300">·</span>
            <span className="text-fuchsia-700">CO {fmtBal(balance.co_balance)}</span>
          </div>
        )}
        {emp.display_location && (
          <div className="text-[9px] text-slate-400 truncate max-w-[160px]" title={emp.display_location}>
            {emp.display_location}
          </div>
        )}
      </td>
      {days.map(d => {
        const dt = new Date(d + 'T00:00:00');
        const isSunday = dt.getDay() === 0;
        const st = rowData?.[d];
        const cellColor = (() => {
          if (!st) return isSunday ? 'bg-slate-100 text-slate-300' : 'bg-slate-50 text-slate-400';
          if (st === 'P' && isSunday) return 'bg-orange-500 text-white';  // Sunday Present = orange (OT)
          return STATUS_COLORS[st] || 'bg-slate-200 text-slate-500';
        })();
        const pt = punchTimes?.[d];
        const creditHrs = pt?.late_work_credit_hours;
        const creditSuffix = creditHrs ? ` · worked ${pt?.worked_hours ?? '?'} h + ${creditHrs} h late-work credit` : '';
        const offKind = offDayKind?.[d] || (isSunday && !offDayKind ? 'Sunday' : null);
        const coCredit = compOff?.[d];
        const coTip = offKind && ['P', 'WFH', 'H', 'HD'].includes(st)
          ? (coCredit ? `Worked on ${offKind} — Comp off earned: ${coCredit} day` : `Worked on ${offKind}, no comp off (under 4 h)`)
          : null;
        return (
          <td key={d} className="text-center px-0.5 py-1.5">
            <button
              type="button"
              data-testid={`cell-${emp.emp_id}-${d}`}
              title={(st ? (coTip || (slSet.has(d) ? `${STATUS_LABELS[st]} · short leave used` : STATUS_LABELS[st])) : (isSunday ? 'Sunday · click to mark WO or P' : 'Click to mark')) + creditSuffix}
              onClick={(e) => onPick(d, e.currentTarget.getBoundingClientRect())}
              className={`relative w-6 h-6 inline-flex items-center justify-center rounded-sm text-[10px] font-bold cursor-pointer transition-transform hover:scale-110 ${cellColor}`}
            >
              {st || '·'}
              {slSet.has(d) && (
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-teal-400 border border-white" title="Short leave used" data-testid={`cell-sl-badge-${emp.emp_id}-${d}`} />
              )}
            </button>
            {showPunchTimes && (
              <div className="text-[7px] leading-tight text-slate-400 font-mono whitespace-nowrap" data-testid={`cell-times-${emp.emp_id}-${d}`}>
                {pt?.check_in || '—'}
                <br />{pt?.check_out || '—'}
                {creditHrs ? <><br /><span className="text-amber-500">+{creditHrs}h credit</span></> : null}
              </div>
            )}
          </td>
        );
      })}
      <td className="px-2 text-center font-bold text-emerald-600 border-l border-slate-200">{s.P || 0}</td>
      <td className="px-2 text-center font-bold text-red-600">{s.A || 0}</td>
      <td className="px-2 text-center font-bold text-amber-600">{s.H || 0}</td>
      <td className="px-2 text-center font-bold text-violet-600">{s.EL || 0}</td>
      <td className="px-2 text-center font-bold text-violet-700">{s.PL || 0}</td>
      <td className="px-2 text-center font-bold text-fuchsia-700">{s.CO || 0}</td>
      <td className="px-2 text-center font-semibold text-zinc-700">{s.LWP_DAYS || 0}</td>
      <td className="px-2 text-center font-semibold text-slate-500">{s.WO_COUNT || 0}</td>
      <td className="px-2 text-center font-semibold text-sky-700">{s.HO_COUNT || 0}</td>
      <td className="px-2 text-center font-extrabold text-[#3b4872]">{
        typeof s.TPD === 'number' ? s.TPD.toFixed(s.TPD % 1 === 0 ? 0 : 1) : (s.TPD || 0)
      }</td>
      <td className="px-2 text-center font-semibold text-amber-600">{s.SUN ?? 0}</td>
      <td className="px-2 text-center font-semibold text-teal-700" data-testid={`att-sl-${emp.emp_id}`}>{s.SHORT_LEAVE_USED || 0}/2</td>
    </tr>
  );
});

function StatusPicker({ employee, projNameById, date, rect, current, punchTimes, onPick, onClear, onClose }) {
  const [siteStep, setSiteStep] = useState(null); // status awaiting a site pick (multi-site employee)
  const PRESENCE_CODES = ['P', 'H', 'WFH'];
  const sites = React.useMemo(() => {
    const ids = [...(employee?.project_ids || [])];
    if (employee?.project_id && !ids.includes(employee.project_id)) ids.unshift(employee.project_id);
    return ids.filter(Boolean);
  }, [employee]);

  const handlePick = (st) => {
    if (PRESENCE_CODES.includes(st) && sites.length > 1) {
      setSiteStep(st);
      return;
    }
    onPick(st);
  };

  // Position popover near the clicked cell
  const style = rect ? {
    position: 'fixed',
    top: Math.min(rect.bottom + 6, window.innerHeight - 320),
    left: Math.min(rect.left, window.innerWidth - 280),
    zIndex: 50,
  } : { position: 'fixed', top: 80, left: 80, zIndex: 50 };

  // Close on outside click / Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDoc = (e) => {
      if (!e.target.closest('[data-picker-root]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [onClose]);

  if (siteStep) {
    return (
      <div data-picker-root data-testid="site-picker" style={style} className="bg-white border border-slate-200 rounded-md shadow-xl p-3 w-[260px]">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Which site?</div>
        <div className="text-xs font-semibold text-slate-800 mb-2">
          {employee?.name} is assigned to {sites.length} sites — pick one for {STATUS_LABELS[siteStep]}
        </div>
        <div className="space-y-1 mb-2 max-h-48 overflow-auto">
          {sites.map(pid => (
            <button
              key={pid}
              type="button"
              data-testid={`site-picker-option-${pid}`}
              onClick={() => onPick(siteStep, pid)}
              className="w-full text-left px-2 py-1.5 rounded-sm text-[11px] border border-slate-200 hover:border-[#3b4872] hover:bg-slate-50"
            >
              {projNameById?.[pid] || pid}
            </button>
          ))}
        </div>
        <button
          type="button"
          data-testid="site-picker-back"
          onClick={() => setSiteStep(null)}
          className="w-full text-center text-[10px] text-slate-500 hover:text-slate-700 mt-1 py-1"
        >
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div data-picker-root data-testid="status-picker" style={style} className="bg-white border border-slate-200 rounded-md shadow-xl p-3 w-[260px]">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Mark Attendance</div>
      <div className="text-xs font-semibold text-slate-800 mb-2">{employee?.name} · <span className="text-slate-500 font-normal">{date}</span></div>
      {punchTimes && (punchTimes.check_in || punchTimes.check_out) ? (
        <div className="flex gap-3 text-[11px] mb-3 px-2 py-1.5 bg-slate-50 rounded border border-slate-100">
          <span>
            <span className="text-slate-400">In — </span>
            <strong className="text-emerald-700">{punchTimes.check_in || '--:--'}</strong>
          </span>
          <span>
            <span className="text-slate-400">Out — </span>
            <strong className="text-slate-700">{punchTimes.check_out || '--:--'}</strong>
          </span>
        </div>
      ) : (
        <div className="text-[11px] text-slate-400 mb-3 px-2 py-1.5 bg-slate-50 rounded border border-slate-100">
          No punch record for this date
        </div>
      )}
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {STATUS_ORDER.map(st => (
          <button
            key={st}
            type="button"
            data-testid={`picker-${st}`}
            onClick={() => handlePick(st)}
            title={STATUS_LABELS[st]}
            className={`px-2 py-1.5 rounded-sm text-[11px] font-bold border ${current === st ? 'ring-2 ring-[#3b4872] ' : 'border-slate-200 hover:border-slate-400'} ${STATUS_COLORS[st]}`}
          >
            <div className="font-mono leading-none">{st}</div>
            <div className="text-[8px] font-medium mt-0.5 opacity-90 truncate">{STATUS_LABELS[st]}</div>
          </button>
        ))}
      </div>
      <button onClick={onClose} className="w-full text-center text-[10px] text-slate-500 hover:text-slate-700 mt-1 py-1">
        Cancel
      </button>
      {current && onClear && (
        <button
          type="button"
          onClick={onClear}
          data-testid="picker-clear"
          className="w-full mt-1 text-center text-[11px] font-bold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 rounded-sm py-1.5 transition-colors"
          title="Clear this attendance entry (undo accidental mark)"
        >
          Clear ({current})
        </button>
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-2 text-[10px] text-slate-600 mt-2">
      <span className="text-slate-500 mr-2 font-semibold">Click any cell to mark:</span>
      {STATUS_ORDER.map((k) => (
        <div key={k} className="flex items-center gap-1">
          <span className={`w-4 h-4 rounded-sm flex items-center justify-center text-[9px] font-bold ${STATUS_COLORS[k]}`}>{k}</span>
          <span>{STATUS_LABELS[k]}</span>
        </div>
      ))}
      <div className="flex items-center gap-1">
        <span className="w-4 h-4 rounded-sm flex items-center justify-center text-[9px] font-bold bg-orange-500 text-white">P</span>
        <span className="text-slate-500">Present (Sunday / OT)</span>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Attendance Import Wizard — multi-step dialog
// Step 0: Upload file + pick month
// Step 1: Preview results (matches, errors, conflicts) + configure options
// Step 2: Done — show result stats
// ════════════════════════════════════════════════════════════════════════
function AttendanceImportWizard({ isOpen, onClose, currentMonth, onImportSuccess }) {
  const STEPS = ['Upload', 'Preview', 'Done'];
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [conflictMode, setConflictMode] = useState('update');
  const [autoWO, setAutoWO] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setFile(null);
      setMonth(currentMonth);
      setPreview(null);
      setImportResult(null);
    }
  }, [isOpen, currentMonth]);

  const handleFile = (f) => { if (f) setFile(f); };

  const analyzeFile = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/hr/attendance/preview`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Preview failed');
      setPreview(data);
      if (data.detected_month) setMonth(data.detected_month);
      setStep(1);
    } catch (err) {
      toast.error(err.message || 'Failed to analyze file');
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = new URLSearchParams({
        month,
        conflict_mode: conflictMode,
        auto_weekly_off: autoWO ? 'true' : 'false',
      });
      const r = await fetch(`${API}/api/hr/attendance/import?${params}`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const result = await r.json();
      if (!r.ok) throw new Error(result.detail || 'Import failed');
      setImportResult(result);
      setStep(2);
      onImportSuccess(result);
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  const summary = preview?.summary || {};
  const unmatchedCount = summary.unmatched_emp_codes?.length || 0;
  const invalidCount = summary.invalid_cells || 0;
  const conflictCount = summary.conflict_cells || 0;

  // Flatten invalid values from all preview rows for the error table
  const allInvalidCells = (preview?.rows || [])
    .filter(r => r.invalid_values?.length > 0)
    .flatMap(r => r.invalid_values.map(iv => ({ emp_code: r.emp_code, ...iv })));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="import-wizard">
      <div
        className="bg-white rounded-lg w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div>
            <h2 className="font-display font-bold text-lg text-slate-800">Import Attendance</h2>
            <div className="flex items-center gap-1.5 mt-2">
              {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                  <span
                    className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full transition-colors ${
                      i === step ? 'bg-[#3b4872] text-white' :
                      i < step  ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
                    }`}
                    data-testid={`wizard-step-${i}`}
                  >
                    {i < step && <Check size={9} className="inline mr-0.5 -mt-0.5" />}{s}
                  </span>
                  {i < STEPS.length - 1 && <span className="text-slate-300 text-xs">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded"
            data-testid="wizard-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ─── Step 0: Upload ─── */}
          {step === 0 && (
            <>
              {/* Drop zone */}
              <div
                data-testid="wizard-dropzone"
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => {
                  e.preventDefault(); setDragOver(false);
                  const f = e.dataTransfer.files[0]; if (f) handleFile(f);
                }}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-[#3b4872] bg-indigo-50' : 'border-slate-300 hover:border-[#3b4872] hover:bg-slate-50'
                }`}
              >
                <Upload size={28} className="mx-auto text-slate-400 mb-3" />
                {file ? (
                  <div>
                    <div className="font-medium text-slate-800 text-sm">{file.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{(file.size / 1024).toFixed(0)} KB · Click or drop to change</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm text-slate-600 font-medium">Drop an Excel file here, or click to browse</div>
                    <div className="text-xs text-slate-400 mt-1">.xlsx · .xls · .xlsm · .csv · max 10 MB</div>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.csv"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
                  data-testid="wizard-file-input"
                />
              </div>

              {/* Month selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600 block">
                  Target Month
                  <span className="text-slate-400 font-normal ml-1">(you can override after preview if the file says otherwise)</span>
                </label>
                <input
                  type="month"
                  value={month}
                  onChange={e => setMonth(e.target.value)}
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3b4872]/40"
                  data-testid="wizard-month"
                />
              </div>

              <div className="text-xs text-slate-400 bg-slate-50 rounded-md p-3 leading-relaxed border border-slate-200">
                <strong className="text-slate-500">Tip:</strong> Use the <strong>Template</strong> button on the roster to download a pre-filled sheet for the correct month. The wizard will show you a full preview before writing any data.
              </div>
            </>
          )}

          {/* ─── Step 1: Preview & Configure ─── */}
          {step === 1 && preview && (
            <>
              {/* Month row + mismatch warning */}
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-600 block">Import into Month</label>
                  <input
                    type="month"
                    value={month}
                    onChange={e => setMonth(e.target.value)}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3b4872]/40"
                    data-testid="wizard-month-step2"
                  />
                </div>
                {preview.detected_month && preview.detected_month !== month && (
                  <div
                    className="flex items-center gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                    data-testid="wizard-month-mismatch"
                  >
                    <AlertTriangle size={12} className="shrink-0" />
                    File says <strong className="mx-1">{preview.detected_month}</strong> — wizard will use <strong className="ml-1">{month}</strong>.
                  </div>
                )}
                {preview.detected_month && preview.detected_month === month && (
                  <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="wizard-month-match">
                    Month matches file (<strong>{month}</strong>).
                  </div>
                )}
                {!preview.detected_month && (
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    No month detected in file — using your selection (<strong>{month}</strong>).
                  </div>
                )}
              </div>

              {/* Header-not-found error */}
              {!preview.header_found && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-300 rounded-md p-4 text-xs text-red-900" data-testid="wizard-no-header">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5 text-red-600" />
                  <div>
                    <strong className="block mb-1">Emp Code header row not found.</strong>
                    The importer looks for a column named &ldquo;Emp Code&rdquo;, &ldquo;Emp_ID&rdquo;, or &ldquo;Employee Code&rdquo;.
                    Use the <strong>Template</strong> button on the roster to download the correct format.
                    {preview.raw_headers?.length > 0 && (
                      <div className="mt-1.5 text-red-700">
                        <span className="font-semibold">Columns detected: </span>
                        {preview.raw_headers.slice(0, 12).filter(Boolean).join(', ')}
                        {preview.raw_headers.length > 12 && ` …and ${preview.raw_headers.length - 12} more`}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Summary stat cards */}
              {preview.header_found && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-center" data-testid="wizard-stat-matched">
                    <div className="text-2xl font-display font-extrabold text-emerald-700">{summary.matched_employees || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-600 mt-0.5">Matched</div>
                  </div>
                  <div className={`border rounded-md p-3 text-center ${unmatchedCount > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`} data-testid="wizard-stat-unmatched">
                    <div className={`text-2xl font-display font-extrabold ${unmatchedCount > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{unmatchedCount}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Unmatched</div>
                  </div>
                  <div className="border border-slate-200 bg-slate-50 rounded-md p-3 text-center" data-testid="wizard-stat-valid">
                    <div className="text-2xl font-display font-extrabold text-slate-700">{summary.valid_cells || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Valid Cells</div>
                  </div>
                  <div className={`border rounded-md p-3 text-center ${invalidCount > 0 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`} data-testid="wizard-stat-invalid">
                    <div className={`text-2xl font-display font-extrabold ${invalidCount > 0 ? 'text-red-600' : 'text-slate-400'}`}>{invalidCount}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Invalid Cells</div>
                  </div>
                </div>
              )}

              {/* Conflicts banner */}
              {conflictCount > 0 && (
                <div className="flex items-center gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2" data-testid="wizard-conflicts">
                  <AlertTriangle size={12} className="shrink-0" />
                  <span>
                    <strong>{conflictCount}</strong> cells already have records for <strong>{month}</strong>.
                    Choose &ldquo;Update&rdquo; to overwrite or &ldquo;Skip&rdquo; to preserve existing data below.
                  </span>
                </div>
              )}

              {/* M3 §6: before-opening notice */}
              {(summary.before_opening_cells || 0) > 0 && (
                <div className="flex items-center gap-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2" data-testid="wizard-before-opening">
                  <AlertTriangle size={12} className="shrink-0" />
                  <span>
                    <strong>{summary.before_opening_cells}</strong> leave cell(s) are on/before HR's opening-balance date —
                    the attendance day will be set, but balance will <strong>not</strong> be deducted (already inside the opening sheet).
                  </span>
                </div>
              )}

              {/* Unmatched codes */}
              {unmatchedCount > 0 && (
                <div data-testid="wizard-unmatched">
                  <div className="text-xs font-semibold text-amber-700 mb-1.5">
                    Unmatched Emp Codes — {unmatchedCount} row{unmatchedCount > 1 ? 's' : ''} will be skipped:
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 border border-amber-200 rounded-md bg-amber-50">
                    {summary.unmatched_emp_codes.map(c => (
                      <span key={c} className="inline-flex text-[11px] font-mono bg-amber-100 text-amber-800 border border-amber-300 rounded px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Invalid cell values */}
              {invalidCount > 0 && (
                <div data-testid="wizard-invalid-cells">
                  <div className="text-xs font-semibold text-red-700 mb-1.5">
                    Invalid Values — {invalidCount} cell{invalidCount > 1 ? 's' : ''} will be skipped during import:
                  </div>
                  <div className="border border-red-200 rounded-md overflow-hidden">
                    <div className="overflow-y-auto max-h-44">
                      <table className="w-full text-xs">
                        <thead className="bg-red-100 border-b border-red-200 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700">Emp Code</th>
                            <th className="text-center px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700">Day</th>
                            <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700">Value Found</th>
                            <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-700">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="bg-red-50">
                          {allInvalidCells.slice(0, 60).map((iv, i) => (
                            <tr key={i} className="border-b border-red-100 last:border-0">
                              <td className="px-3 py-1.5 font-mono text-red-700">{iv.emp_code}</td>
                              <td className="px-3 py-1.5 text-center text-slate-600">{iv.day}</td>
                              <td className="px-3 py-1.5 font-mono font-bold text-red-600">{iv.value}</td>
                              <td className="px-3 py-1.5 text-slate-600">{iv.reason || 'invalid code'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Valid codes: <span className="font-mono">{preview.valid_codes?.join(' · ')}</span>
                    {allInvalidCells.length > 60 && ` · showing first 60 of ${allInvalidCells.length}`}
                  </div>
                </div>
              )}

              {/* Import options */}
              {preview.header_found && (
                <div className="border border-slate-200 rounded-md p-4 bg-slate-50/60 space-y-3">
                  <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Import Options</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-600 w-36 shrink-0">Existing records:</span>
                    <button
                      onClick={() => setConflictMode('update')}
                      data-testid="wizard-opt-update"
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${conflictMode === 'update' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                    >
                      Update (overwrite)
                    </button>
                    <button
                      onClick={() => setConflictMode('skip')}
                      data-testid="wizard-opt-skip"
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${conflictMode === 'skip' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                    >
                      Skip (keep existing)
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-600 w-36 shrink-0">Blank Sundays:</span>
                    <button
                      onClick={() => setAutoWO(!autoWO)}
                      data-testid="wizard-opt-auto-wo"
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${autoWO ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-100'}`}
                    >
                      {autoWO ? 'Auto-fill as WO (on)' : 'Leave blank (off)'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 2: Done */}
          {step === 2 && importResult && (
            <div className="space-y-4" data-testid="wizard-done">
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <Check size={22} className="text-emerald-600 shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-800 text-base">Import Successful</div>
                  <div className="text-sm text-emerald-700 mt-0.5">Month: <strong>{importResult.month}</strong></div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="border rounded-md p-3 bg-slate-50 text-center">
                  <div className="text-2xl font-display font-extrabold text-[#3b4872]" data-testid="wizard-result-cells">{importResult.cells_updated}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Cells Written</div>
                </div>
                <div className="border rounded-md p-3 bg-slate-50 text-center">
                  <div className="text-2xl font-display font-extrabold text-emerald-700" data-testid="wizard-result-emps">{importResult.employees_matched}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Employees</div>
                </div>
                {importResult.cells_skipped_conflicts > 0 && (
                  <div className="border border-blue-200 rounded-md p-3 bg-blue-50 text-center">
                    <div className="text-2xl font-display font-extrabold text-blue-700">{importResult.cells_skipped_conflicts}</div>
                    <div className="text-[10px] uppercase tracking-widest text-blue-600 mt-0.5">Skipped (conflict)</div>
                  </div>
                )}
                {importResult.comp_off_earned > 0 && (
                  <div className="border border-amber-200 rounded-md p-3 bg-amber-50 text-center">
                    <div className="text-2xl font-display font-extrabold text-amber-700">{importResult.comp_off_earned}</div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-600 mt-0.5">Comp-Offs Earned</div>
                  </div>
                )}
                {importResult.before_opening_count > 0 && (
                  <div className="border border-indigo-200 rounded-md p-3 bg-indigo-50 text-center" data-testid="wizard-result-before-opening">
                    <div className="text-2xl font-display font-extrabold text-indigo-700">{importResult.before_opening_count}</div>
                    <div className="text-[10px] uppercase tracking-widest text-indigo-600 mt-0.5">Before Opening (day set, no deduction)</div>
                  </div>
                )}
              </div>
              {importResult.month_mismatch && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  File was for <strong>{importResult.month_from_file}</strong> but imported into <strong>{importResult.month}</strong> per your selection.
                </div>
              )}
              {importResult.rule_errors_count > 0 && (
                <div data-testid="wizard-rule-errors">
                  <div className="text-xs font-semibold text-red-700 mb-1.5">
                    Skipped (rule conflict) — {importResult.rule_errors_count} cell{importResult.rule_errors_count > 1 ? 's' : ''} not written:
                  </div>
                  <div className="border border-red-200 rounded-md overflow-hidden max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody className="bg-red-50">
                        {(importResult.rule_errors || []).map((re, i) => (
                          <tr key={i} className="border-b border-red-100 last:border-0">
                            <td className="px-3 py-1.5 font-mono text-red-700">{re.emp_code}</td>
                            <td className="px-3 py-1.5 text-slate-600">{re.date}</td>
                            <td className="px-3 py-1.5 font-mono font-bold text-red-600">{re.value}</td>
                            <td className="px-3 py-1.5 text-slate-600">{re.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {importResult.skipped_codes?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1.5">Skipped Emp Codes (not in employee master):</div>
                  <div className="flex flex-wrap gap-1.5">
                    {importResult.skipped_codes.map(c => (
                      <span key={c} className="text-[11px] font-mono bg-slate-100 text-slate-600 border border-slate-200 rounded px-2 py-0.5">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-between items-center shrink-0">
          {step === 0 && (
            <>
              <Button variant="outline" onClick={onClose} data-testid="wizard-cancel">Cancel</Button>
              <Button onClick={analyzeFile} disabled={!file || loading} className="bg-[#3b4872] hover:bg-[#2c3658] disabled:opacity-50" data-testid="wizard-analyze-btn">
                {loading && <Loader2 size={14} className="animate-spin mr-2" />}
                {loading ? 'Analyzing…' : 'Analyze File →'}
              </Button>
            </>
          )}
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => setStep(0)} data-testid="wizard-back">← Back</Button>
              <Button onClick={doImport} disabled={!preview?.header_found || importing} className="bg-[#3b4872] hover:bg-[#2c3658] disabled:opacity-50" data-testid="wizard-import-btn">
                {importing && <Loader2 size={14} className="animate-spin mr-2" />}
                {!importing && <Upload size={14} className="mr-2" />}
                {importing ? 'Importing…' : `Import ${summary.valid_cells || 0} cells into ${month}`}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <div />
              <Button onClick={onClose} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="wizard-done-btn">
                <Check size={14} className="mr-2" /> Done
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LeaveWorkflow() {
  const { user: currentUser } = useAuth() || {};
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApply, setShowApply] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('');
  const [fDept, setFDept] = useState('');
  const [fProj, setFProj] = useState('');
  const [fLoc, setFLoc] = useState('');
  const [fQuery, setFQuery] = useState('');
  const [form, setForm] = useState({ employee_id: '', leave_type: 'EL', from_date: '', to_date: '', reason: '', medical_cert_url: '' });
  const [policy, setPolicy] = useState(null);
  const [balance, setBalance] = useState(null);
  const [uploadingCert, setUploadingCert] = useState(false);
  // M2 · revoke an approved leave + persistent post-approve notice
  const [revokeFor, setRevokeFor] = useState(null);   // leave being revoked
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [approveNotice, setApproveNotice] = useState(null); // { name, dates: [] }
  // SA1/SA2 · super_admin self-approval override — mandatory reason prompt
  const [selfApproveFor, setSelfApproveFor] = useState(null); // { leaveId, action }
  const [selfApproveReason, setSelfApproveReason] = useState('');
  const [selfApproving, setSelfApproving] = useState(false);
  const isSuperAdmin = (currentUser?.role || '').toLowerCase() === 'super_admin';
  const isOwnLeave = (lv) => Boolean(
    lv?.employee_id && (lv.employee_id === currentUser?.linked_employee_uuid)
  ) || Boolean(lv?.emp_code && lv.emp_code === currentUser?.employee_id);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/hr/leaves`, { credentials: 'include' });
      const d = await r.json();
      setLeaves(d || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetch(`${API}/api/hr/employees`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setEmployees(d || []))
      .catch(() => {});
    fetch(`${API}/api/hr/leaves/policy`, { credentials: 'include' })
      .then(r => r.json()).then(d => setPolicy(d)).catch(() => {});
  }, [fetchData]);

  useEffect(() => {
    if (!form.employee_id) { setBalance(null); return; }
    fetch(`${API}/api/hr/leaves/balance?employee_id=${form.employee_id}`, { credentials: 'include' })
      .then(r => r.json()).then(setBalance).catch(() => setBalance(null));
  }, [form.employee_id]);

  const previewDays = (() => {
    if (!form.from_date || !form.to_date) return 0;
    const a = new Date(form.from_date), b = new Date(form.to_date);
    return Math.max(0, Math.floor((b - a) / 86400000) + 1);
  })();
  const isLongLeave = previewDays > 5;
  const [showLongLeaveConfirm, setShowLongLeaveConfirm] = useState(false);

  const typeBalance = balance?.balance?.find((b) => b.leave_type === form.leave_type);
  const slNeedsCert = form.leave_type === 'SL' && previewDays > 2;

  const uploadCert = async (file) => {
    if (!file) return;
    setUploadingCert(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/hr/leaves/upload-medical-cert`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Upload failed');
      setForm(f => ({ ...f, medical_cert_url: data.url }));
      toast.success('Medical certificate uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingCert(false);
    }
  };

  const apply = async () => {
    if (!form.employee_id || !form.from_date || !form.to_date) {
      toast.error('Employee and dates required');
      return;
    }
    if (isLongLeave && !showLongLeaveConfirm) {
      setShowLongLeaveConfirm(true);
      return;
    }
    const r = await fetch(`${API}/api/hr/leaves`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(form),
    });
    if (r.ok) {
      toast.success('Leave request submitted');
      setShowApply(false);
      setShowLongLeaveConfirm(false);
      setForm({ employee_id: '', leave_type: 'CL', from_date: '', to_date: '', reason: '', medical_cert_url: '' });
      fetchData();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to apply');
    }
  };

  const decide = async (leaveId, action, reason) => {
    const isApprove = action === 'approve';
    const url = isApprove
      ? `${API}/api/hr/leaves/${leaveId}/${action}`
      : `${API}/api/hr/leaves/${leaveId}/${action}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`;
    const r = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      ...(isApprove ? {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reason ? { reason } : {}),
      } : {}),
    });
    if (r.ok) {
      toast.success(`Leave ${action === 'approve' ? 'approved' : 'rejected'}`);
      // M7: surface any days that had a Present punch and were left unchanged
      // as a PERSISTENT notice (toasts vanish before people read them).
      if (action === 'approve') {
        const data = await r.json().catch(() => ({}));
        const skipped = data?.skipped_present || [];
        if (skipped.length) {
          const lv = leaves.find(x => x.id === leaveId);
          setApproveNotice({ name: lv?.employee_name || 'Employee', dates: skipped });
        }
      }
      fetchData();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e?.detail || `Failed to ${action}`);
    }
  };

  const handleDecideClick = (lv, action) => {
    if (isSuperAdmin && isOwnLeave(lv)) {
      setSelfApproveFor({ leaveId: lv.id, action });
      return;
    }
    decide(lv.id, action);
  };

  const confirmSelfApprove = async () => {
    if (!selfApproveFor) return;
    setSelfApproving(true);
    try {
      await decide(selfApproveFor.leaveId, selfApproveFor.action, selfApproveReason.trim());
      setSelfApproveFor(null);
      setSelfApproveReason('');
    } finally {
      setSelfApproving(false);
    }
  };

  const doRevoke = async () => {
    if (!revokeFor || !revokeReason.trim()) { toast.error('A reason is required to revoke'); return; }
    setRevoking(true);
    try {
      const r = await fetch(
        `${API}/api/hr/leaves/${revokeFor.id}/revoke?reason=${encodeURIComponent(revokeReason.trim())}`,
        { method: 'PUT', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || 'Failed to revoke');
      const n = (data.attendance_reversed || []).length;
      toast.success(`Leave revoked${n ? ` · ${n} attendance day${n === 1 ? '' : 's'} reversed` : ''}`);
      setRevokeFor(null); setRevokeReason('');
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to revoke');
    } finally {
      setRevoking(false);
    }
  };

  const depts = Array.from(new Set(leaves.map(l => l.department).filter(Boolean))).sort();
  const projs = Array.from(
    new Map(
      leaves
        .filter(l => l.project_id)
        .map(l => [l.project_id, { id: l.project_id, name: l.project_name || l.project_id }])
    ).values()
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const locs = Array.from(new Set(leaves.map(l => l.work_location).filter(Boolean))).sort();

  const filtered = leaves.filter(l => {
    if (filter && l.status !== filter) return false;
    if (fDept && (l.department || '') !== fDept) return false;
    if (fProj && (l.project_id || '') !== fProj) return false;
    if (fLoc && (l.work_location || '') !== fLoc) return false;
    if (fQuery) {
      const q = fQuery.toLowerCase();
      const hay = `${l.employee_name || ''} ${l.emp_code || ''} ${l.reason || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const clearAdvFilters = () => { setFDept(''); setFProj(''); setFLoc(''); setFQuery(''); };
  const hasAdvFilter = !!(fDept || fProj || fLoc || fQuery);

  const exportFilteredCsv = () => {
    if (filtered.length === 0) { toast.error('No rows to export'); return; }
    const header = ['Employee', 'Emp Code', 'Department', 'Project', 'Location', 'Leave Type', 'From', 'To', 'Days', 'Status', 'Reason'];
    const csvRows = [
      header.join(','),
      ...filtered.map(l => [
        l.employee_name, l.emp_code, l.department, l.project_name, l.work_location,
        l.leave_type, l.from_date, l.to_date, l.days, l.status, (l.reason || '').replace(/\n/g, ' ')
      ].map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-workflow-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} row${filtered.length === 1 ? '' : 's'}`);
  };

  return (
    <div className="space-y-3">
      {approveNotice && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="leave-present-punch-notice">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <div className="font-semibold">Approved — but some days were left unchanged.</div>
            <div className="text-xs mt-0.5">
              {approveNotice.name} has a Present punch on these day{approveNotice.dates.length === 1 ? '' : 's'}, so attendance was NOT changed to Paid Leave:{' '}
              <span className="font-mono font-semibold">{approveNotice.dates.join(', ')}</span>. Review them manually.
            </div>
          </div>
          <button onClick={() => setApproveNotice(null)} className="text-amber-700 hover:text-amber-900 shrink-0" data-testid="leave-present-punch-notice-close"><X size={16} /></button>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {['', 'Pending', 'Approved', 'Rejected'].map(s => (
            <button
              key={s || 'all'}
              onClick={() => setFilter(s)}
              data-testid={`leave-filter-${s || 'all'}`}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                filter === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportFilteredCsv}
            data-testid="leave-export-csv"
            className="text-xs px-3 py-1.5 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5"
          >
            <Download size={12} /> Export ({filtered.length})
          </button>
          <Button onClick={() => setShowApply(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="apply-leave-btn">
            <Plus size={14} className="mr-1.5" /> Apply Leave
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="leave-filter-row">
        <input
          type="text"
          value={fQuery}
          onChange={(e) => setFQuery(e.target.value)}
          placeholder="Search name / emp-code / reason…"
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs w-56 focus:outline-none focus:border-[#3b4872]"
          data-testid="leave-filter-search"
        />
        <select
          value={fDept}
          onChange={(e) => setFDept(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
          data-testid="leave-filter-department"
        >
          <option value="">All Departments ({depts.length})</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select
          value={fProj}
          onChange={(e) => setFProj(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white max-w-[260px]"
          data-testid="leave-filter-project"
        >
          <option value="">All Projects ({projs.length})</option>
          {projs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select
          value={fLoc}
          onChange={(e) => setFLoc(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-1.5 text-xs bg-white"
          data-testid="leave-filter-location"
        >
          <option value="">All Locations ({locs.length})</option>
          {locs.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {hasAdvFilter && (
          <button onClick={clearAdvFilters} className="text-[11px] text-slate-500 hover:text-rose-600 underline ml-1" data-testid="leave-filter-clear">
            Clear filters
          </button>
        )}
        <span className="text-[11px] text-slate-500 ml-auto" data-testid="leave-filter-count">
          <span className="font-semibold text-slate-700">{filtered.length}</span> of {leaves.length} requests
        </span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5">Employee</th>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">From</th>
                  <th className="text-left px-3 py-2.5">To</th>
                  <th className="text-center px-3 py-2.5">Days</th>
                  <th className="text-left px-3 py-2.5">Reason</th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-500">No leave records.</td></tr>
                ) : filtered.map(l => (
                  <tr key={l.id} className="border-b border-slate-100" data-testid={`leave-row-${l.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{l.employee_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{l.emp_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 rounded-sm">{l.leave_type}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{l.from_date}</td>
                    <td className="px-3 py-2 font-mono text-xs">{l.to_date}</td>
                    <td className="px-3 py-2 text-center font-bold">{l.days}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs max-w-xs truncate" title={l.reason}>{l.reason || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-sm font-bold ${
                        l.status === 'Approved' ? 'bg-emerald-50 text-emerald-700' :
                        l.status === 'Rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                      }`}>{l.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {l.status === 'Pending' ? (
                        <>
                          <button onClick={() => handleDecideClick(l, 'approve')} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded mr-1" data-testid={`approve-${l.id}`} title="Approve">
                            <Check size={14} />
                          </button>
                          <button onClick={() => handleDecideClick(l, 'reject')} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`reject-${l.id}`} title="Reject">
                            <X size={14} />
                          </button>
                        </>
                      ) : l.status === 'Approved' ? (
                        <button onClick={() => { setRevokeFor(l); setRevokeReason(''); }} className="inline-flex items-center gap-1 text-[11px] text-rose-600 hover:bg-rose-50 border border-rose-200 px-2 py-1 rounded" data-testid={`revoke-${l.id}`} title="Revoke this approved leave">
                          <RotateCcw size={12} /> Revoke
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-400">{l.approver || '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showApply && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="leave-apply-modal">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-display font-bold text-lg">Apply for Leave</h3>
              <button onClick={() => setShowApply(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="Employee">
                <select value={form.employee_id} onChange={(e) => setForm(f => ({ ...f, employee_id: e.target.value }))} className={inp}>
                  <option value="">Select employee</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{`${e.emp_id} · ${e.name}`}</option>)}
                </select>
              </Field>
              <Field label="Leave Type">
                <select value={form.leave_type} onChange={(e) => setForm(f => ({ ...f, leave_type: e.target.value, medical_cert_url: '' }))} className={inp} data-testid="leave-type-select">
                  {policy?.policy && Object.entries(policy.policy).map(([code, p]) => (
                    <option key={code} value={code}>{p.name} ({code})</option>
                  ))}
                  {!policy && (
                    <>
                      <option value="CL">Casual Leave (CL)</option>
                      <option value="SL">Sick Leave (SL)</option>
                      <option value="PL">Privilege Leave (PL)</option>
                      <option value="LWP">Leave Without Pay (LWP)</option>
                    </>
                  )}
                </select>
                {typeBalance && (
                  <div className={`mt-1 text-[11px] ${typeBalance.eligible ? 'text-slate-500' : 'text-amber-700'}`}>
                    {typeBalance.eligible ? (
                      <>Quota {typeBalance.used}/{typeBalance.quota} used · <strong>{typeBalance.remaining}</strong> {typeBalance.unit || 'days'} left</>
                    ) : (
                      <>⚠ {typeBalance.ineligibility_reason}</>
                    )}
                  </div>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="From"><input type="date" value={form.from_date} onChange={(e) => setForm(f => ({ ...f, from_date: e.target.value }))} className={inp} /></Field>
                <Field label="To"><input type="date" value={form.to_date} onChange={(e) => setForm(f => ({ ...f, to_date: e.target.value }))} className={inp} /></Field>
              </div>
              <Field label="Reason">
                <textarea rows={2} value={form.reason} onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))} className={inp} />
              </Field>

              {slNeedsCert && (
                <Field label={`Medical Certificate · required for SL > 2 days${form.medical_cert_url ? ' ✓' : ''}`}>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => uploadCert(e.target.files?.[0])}
                    disabled={uploadingCert}
                    data-testid="leave-medical-cert"
                    className="text-xs"
                  />
                  {form.medical_cert_url && (
                    <div className="text-[11px] text-emerald-700 font-mono mt-1 truncate">{form.medical_cert_url.split('/').pop()}</div>
                  )}
                </Field>
              )}

              {isLongLeave && (
                <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded-sm px-3 py-2" data-testid="leave-long-leave-banner">
                  <strong>{previewDays} days</strong> · This is a long leave. Please consult your supervisor and the HR team before applying.
                  <span className="block text-[10px] text-amber-700 mt-0.5">Sandwich rule may further reduce chargeable days when weekends/holidays fall inside the range.</span>
                </div>
              )}
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowApply(false)}>Cancel</Button>
              <Button onClick={apply} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="leave-submit-btn">Submit</Button>
            </div>
          </div>
        </div>
      )}

      {showLongLeaveConfirm && (
        <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setShowLongLeaveConfirm(false)}>
          <div className="bg-white rounded-md w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="leave-long-leave-confirm-modal">
            <div className="p-5 space-y-3">
              <h3 className="font-display font-bold text-base text-amber-800">Long Leave — {previewDays} days</h3>
              <p className="text-sm text-slate-700">This is a long leave. Please consult your supervisor and the HR team before applying.</p>
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLongLeaveConfirm(false)} data-testid="leave-long-leave-cancel-btn">Cancel</Button>
              <Button onClick={apply} className="bg-amber-700 hover:bg-amber-800" data-testid="leave-long-leave-continue-btn">Continue</Button>
            </div>
          </div>
        </div>
      )}

      {revokeFor && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => !revoking && setRevokeFor(null)}>
          <div className="bg-white rounded-md w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="revoke-modal">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-display font-bold text-lg">Revoke Approved Leave</h3>
              <button onClick={() => setRevokeFor(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm text-slate-700">
                Revoking <strong>{revokeFor.employee_name}</strong>'s{' '}
                <strong>{revokeFor.leave_type}</strong> leave ({revokeFor.from_date} → {revokeFor.to_date}, {revokeFor.days}d).
              </div>
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                This reverses the leave-balance debit and puts the attendance days back to what they were before (or to A if they were blank).
              </div>
              <Field label="Reason (required)">
                <textarea rows={3} value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} className={inp} data-testid="revoke-reason-input" placeholder="Why is this approved leave being revoked?" />
              </Field>
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRevokeFor(null)} disabled={revoking}>Cancel</Button>
              <button onClick={doRevoke} disabled={revoking || !revokeReason.trim()} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded text-sm font-bold disabled:opacity-40 flex items-center gap-1.5" data-testid="revoke-confirm-btn">
                {revoking ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Revoke Leave
              </button>
            </div>
          </div>
        </div>
      )}
      {selfApproveFor && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => !selfApproving && setSelfApproveFor(null)}>
          <div className="bg-white rounded-md w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="self-approve-modal">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="font-display font-bold text-lg">Self-approval override</h3>
              <button onClick={() => setSelfApproveFor(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                This is your own leave request. As super_admin you may {selfApproveFor.action} it. A reason is optional but will be recorded in the audit log if provided.
              </div>
              <Field label="Reason (optional)">
                <textarea rows={3} value={selfApproveReason} onChange={(e) => setSelfApproveReason(e.target.value)} className={inp} data-testid="self-approve-reason-input" placeholder={`Optional reason to ${selfApproveFor.action} your own leave…`} />
              </Field>
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelfApproveFor(null)} disabled={selfApproving}>Cancel</Button>
              <button onClick={confirmSelfApprove} disabled={selfApproving} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm font-bold disabled:opacity-40 flex items-center gap-1.5" data-testid="self-approve-confirm-btn">
                {selfApproving ? <Loader2 size={13} className="animate-spin" /> : (selfApproveFor.action === 'approve' ? <Check size={13} /> : <X size={13} />)}
                Confirm {selfApproveFor.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3b4872]/40";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
